import type { EventSkeleton } from '../types/sessions.js';
import type { SendMessageResponse, SessionQueue, SessionQueueItem, SessionTasks, StopMode } from '../types/reasoning.js';
import type { ListOpenQuestionsResponse } from '../types/questions.js';
import { QueuedImageStore, QueuedImageStoreError } from './queued-images.js';
import type { HttpLayer } from '../http.js';
import type { ShogunChopResource } from '../resources/shogunchop.js';
import type { ShogunChopEventStream } from '../resources/shogunchop.js';
import type { BridgeSessionSource } from '../resources/bridge-session-source.js';
import { SessionsResource } from '../resources/sessions.js';
import type {
  ChatMessage, ChatState, ConnectionState, ProgressTree,
  QueuedMessage, ChatQuestion, QuestionAnswer, AnsweredQuestion,
  MessageHandler, ProgressHandler, StateHandler,
  QuestionsHandler, ConnectionHandler, QueueHandler, ErrorHandler,
  ContextHealthHandler,
  ProcessedState, WebSocketFactory, ProgressStep, LiveLlmCall, LlmProgressFrame,
  ImageAttachment, MentionRef, PromptAttachment, PlatformApprovalRequest,
  MediaActionApprovalRequest, MediaActionApprovalResponse,
  ChatSettings, SettingsHandler, PersistedHandler,
  RunsPanelDemand, TranscriptDemand, HistoryGap, TranscriptGap, RagHitRef,
  ChatParticipant, ChatSendOptions, BackgroundWorkItem,
} from './types.js';
import { ParticipantTranscript } from './participant-transcript.js';
import { processEvents } from './event-processor.js';
import { deriveBackgroundWork } from './background-work.js';
import { indexAfter, insertBySequence } from './event-order.js';
import {
  capEvents,
  capMessages,
  deriveHistoryWindowCeiling,
  firstGap,
  retainEvents,
  retainMessages,
  type SeqRange,
} from './history-window.js';
import { uuidV4 } from '../util/uuid.js';
import type { ToolsResource } from '../resources/tools.js';
import { SkillsResource } from '../resources/skills.js';
import { PersonaResource } from '../resources/persona.js';
import type { PersonaAgentConfig, PersonaConfig } from '../types/persona.js';
import type { ContextEntryRead, InternalAgentVisibility, Message, Participant, ParticipantStatus, RunningBackgroundTask, Session, SessionEvent } from '../types/sessions.js';
import type { HistoryCache } from '../cache/history-cache.js';
import { SDKError, isConflict, isNotFound } from '../errors.js';
import { emitLifecycleEvent } from '../lifecycle/observability.js';
import {
  AmbiguousCapabilityError,
  SessionBusyError,
  WorkspaceRequiredError,
} from '../types/capabilities.js';
import type {
  Capability,
  ArgCapture,
  ArgField,
  InvokeOutcome,
  JSONSchema,
} from '../types/capabilities.js';
import type { ClientContext, ViewContext } from '../types/reasoning.js';
import type { ListAvailableSkillsResponse } from '../types/tools.js';

/**
 * Read the calling runtime's IANA timezone for the turn's `client_context`.
 *
 * Returns `undefined` — not a guessed zone, not `'UTC'` — when the runtime
 * cannot name one. That is omit-when-unknown: an absent value lets the
 * backend's precedence fall through to the stored profile timezone, whereas a
 * fabricated `'UTC'` would OUTRANK the profile and tell the agent the user is
 * somewhere they are not.
 *
 * `resolvedOptions()` is specified to always return a `timeZone`, but this runs
 * in browsers, Electron, and Node alike, so the value is checked rather than
 * assumed.
 */
function resolveClientContext(): ClientContext | undefined {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof timeZone === 'string' && timeZone.length > 0
    ? { timezone: timeZone }
    : undefined;
}

interface InvokeCapabilityWireResponse {
  status: string;
  error_kind?: string;
  error?: string;
  call_entry_id?: string;
  result_entry_id?: string;
  message_index?: number;
  preview?: string;
}

function codedOutcomeFromWire(res: InvokeCapabilityWireResponse): InvokeOutcome {
  const outcome: Extract<InvokeOutcome, { kind: 'coded' }> = {
    kind: 'coded',
    status: res.status,
  };
  if (
    res.error_kind === 'execute_failed' ||
    res.error_kind === 'persist_failed_after_execution'
  ) {
    outcome.errorKind = res.error_kind;
  }
  if (typeof res.error === 'string') {
    outcome.error = res.error;
  }
  if (typeof res.preview === 'string') {
    outcome.preview = res.preview;
  }
  if (
    typeof res.call_entry_id === 'string' &&
    typeof res.result_entry_id === 'string' &&
    typeof res.message_index === 'number'
  ) {
    outcome.entryRef = {
      callEntryId: res.call_entry_id,
      resultEntryId: res.result_entry_id,
      messageIndex: res.message_index,
    };
  }
  return outcome;
}


/**
 * Wire shape of the `WorkspaceInitAgentResult` envelope that `workspace_init`
 * JSON-encodes into tool content / claim-check preview.
 */
interface WorkspaceInitAgentWireResult {
  persona: string;
  run_id: string;
  session_id: string;
  started: boolean;
}

function assertWorkspaceInitAgentStarted(preview: string | undefined): void {
  if (!preview) {
    throw new SDKError(
      'workspace_init reported success but returned no WorkspaceInitAgentResult preview',
    );
  }
  let parsed: WorkspaceInitAgentWireResult;
  try {
    parsed = JSON.parse(preview) as WorkspaceInitAgentWireResult;
  } catch {
    throw new SDKError(`workspace_init returned a malformed result envelope: ${preview}`);
  }
  if (parsed.persona !== 'workspace-initializer') {
    throw new SDKError(
      `workspace_init envelope persona must be "workspace-initializer", got: ${preview}`,
    );
  }
  if (parsed.started !== true || typeof parsed.run_id !== 'string' || !parsed.run_id) {
    throw new SDKError(
      `workspace_init must auto-start Agent.Execute (started/run_id missing): ${preview}`,
    );
  }
}


function requiredParams(params?: JSONSchema): string[] {
  if (!params) return [];
  const req = (params as Record<string, unknown>).required;
  if (!Array.isArray(req)) return [];
  return req.filter((x): x is string => typeof x === 'string');
}

/**
 * Coded-tool routing ids that are workspace-scoped (device/repo/cloud-repo
 * bound). Their capabilities cannot run on a workspace-less session; the
 * backend filters them out of a workspace-less discovery set, and `invoke()`
 * additionally rejects them synchronously via `WorkspaceRequiredError`.
 */
const WORKSPACE_SCOPED_MCP_IDS: ReadonlySet<string> = new Set([
  'localagent-unified-tools',
  'localagent-backend-cloud-fs',
]);

/**
 * Names of the workspace-scoped coded capabilities. Used for the synchronous
 * `invoke()` guard when the caller passes a bare name (no `mcpId` available to
 * classify by). This mirrors the server-side discovery filter;
 * it is a defensive guard, not the primary filter — a workspace-less session
 * never surfaces these names in `listCapabilities()`.
 */
const CATALOG_SKILL_TOOL_NAMES: ReadonlySet<string> = new Set([
  'skill_list',
  'skill_search',
  'skill_read',
  'skill_create',
  'skill_update',
  'skill_delete',
  // Model, provider and process lookups are account-scoped too — they resolve
  // against the account catalog and carry no workspace.
  'model_default',
  'provider_list',
  'model_list',
  'process_list',
]);

const WORKSPACE_SCOPED_TOOL_NAMES: ReadonlySet<string> = new Set([
  'file_read',
  'file_write',
  'file_edit',
  'file_append',
  'file_delete',
  'file_move',
  'file_list',
  'file_info',
  'grep',
  'bash',
  'workspace_init',
  'cloud_save_version',
  'cloud_view_unsaved',
  'cloud_list_versions',
  'cloud_restore_version',
  'explore',
  'research',
]);

function participantFromRow(row: Participant): ChatParticipant {
  const label = row.agent_name || row.persona_name || row.system_persona_name;
  if (!label) throw new Error(`participant ${row.id} has no agent, persona or system persona name`);
  return {
    id: row.id,
    isPrimary: row.is_primary,
    label,
    agentId: row.agent_id,
    personaName: row.persona_name ?? row.system_persona_name ?? null,
    ownerParticipantId: row.owner_participant_id,
    spawnTaskId: row.spawn_task_id,
    background: row.background,
    status: row.status,
  };
}

function participantStatusFromEvent(e: EventSkeleton): ParticipantStatus {
  switch (e.event_type) {
    case 'task_started':
      return 'running';
    case 'task_failed':
    case 'reasoning_failed':
      return 'failed';
    case 'execution_stopped':
      return 'stopped';
    case 'task_completed': {
      const status = e.data.status;
      if (status === 'failed' || status === 'stopped') return status;
      if (status === undefined || status === 'completed') return 'settled';
      throw new Error(`task_completed event ${e.sequence_num} carries an unknown status ${JSON.stringify(status)}`);
    }
    case 'participant_status': {
      const status = e.data.status;
      if (status === 'running' || status === 'idle' || status === 'settled' || status === 'failed' || status === 'stopped') return status;
      throw new Error(`participant_status event ${e.sequence_num} carries an unknown status ${String(status)}`);
    }
  }
  throw new Error(`${e.event_type} event ${e.sequence_num} carries no participant status`);
}

interface ParticipantQueueState {
  items: QueuedMessage[];
  version: number;
  handlers: Set<QueueHandler>;
  load: Promise<void> | null;
  loadAgain: boolean;
}

function queuedMessageFromWire(item: SessionQueueItem): QueuedMessage {
  return {
    id: item.id,
    position: item.position,
    clientMessageId: item.client_message_id,
    content: item.content,
    attachments: item.attachments,
    status: item.status,
    blockedReason: item.blocked_reason,
    hasImages: item.has_images,
    authorUserId: item.author_user_id,
    originalAuthorUserId: item.original_author_user_id,
    authorIsViewer: item.author_is_viewer,
    authorLabel: item.author_label,
    originalAuthorLabel: item.original_author_label,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

// (context ADR-31fe6)
const PARTICIPANT_STATUS_EVENTS = new Set(['task_started', 'task_completed', 'task_failed', 'reasoning_failed', 'execution_stopped', 'participant_status']);
const PARTICIPANT_LANE_EVENTS = new Set(['user_message', 'assistant_message', 'llm_call_start', 'task_completed', 'participant_status']);

export class ChatSession {
  private _sessionId?: string;
  private _workspaceId?: string;
  /**
   * The session's device binding. Threaded into capability discovery
   * and invocation so device-routed local tools are listed and execute
   * correctly. May be undefined for cloud-only sessions.
   */
  readonly deviceId?: string;

  get sessionId(): string | undefined { return this._sessionId; }
  get workspaceId(): string | undefined { return this._workspaceId; }
  get isDraft(): boolean { return this._sessionId === undefined; }

  private http: HttpLayer;
  private tools: ToolsResource;
  private skills: SkillsResource;
  private sessions: SessionsResource;
  private personas: PersonaResource;
  private persistImpl?: (session: ChatSession) => Promise<void>;
  private inFlightPersist: Promise<string> | null = null;
  private inFlightOlderEvents: Promise<readonly SessionEvent[]> | null = null;
  private persistedHandlers = new Set<PersistedHandler>();
  private settingsHandlers = new Set<SettingsHandler>();
  private _starting = false;
  private _optimistic: ChatMessage[] = [];
  private _personaId?: string;
  private _processOverride?: string;
  private _automaticOverride?: boolean | null;
  private _pinModelToTurn = false;
  private _internalAgentVisibility: InternalAgentVisibility = 'friendly';
  private _contextRagEnabled?: boolean;
  private _contextRagChildRunsEnabled?: boolean;
  private _processId?: string;
  private _automaticEffective = false;
  private _effectiveModel?: ChatSettings['effectiveModel'];
  // (sdk ADR-f43c7)
  private events: EventSkeleton[] = [];
  private skeletonBySeq = new Map<number, EventSkeleton>();
  private pendingPush: EventSkeleton[] = [];
  // Two watermarks: maxSequenceNum = highest SEEN (gap DETECTION);
  // contiguousMax = highest gapless N (the replay CURSOR for BOTH reconnect and
  // gap re-register — a latent-bug fix). In the no-gap steady state they are equal.
  private maxSequenceNum = 0;
  private contiguousMax = 0;
  // Bounded LRU of hydrated full payloads. Insertion order = LRU
  // order; a re-hydration/touch deletes+re-sets to move the entry to the tail;
  // eviction drops from the head. Only the cold `data` is dropped here; a
  // skeleton leaves only through window retention.
  private payloadCache = new Map<number, Record<string, unknown>>();
  private payloadCacheBytes = new Map<number, number>();
  private payloadCacheTotalBytes = 0;
  private static readonly CACHE_MAX_ENTRIES = 64;
  private static readonly CACHE_MAX_BYTES = 4 * 1024 * 1024; // 4 MiB
  private static readonly DEFAULT_WINDOW_PAGE = 200;
  private readonly windowPage: number;
  private readonly windowCeiling: number;
  private demand: { runsPanel?: RunsPanelDemand | null; transcript?: TranscriptDemand | null } = {};
  private sessionSource: BridgeSessionSource | null = null;
  private gapReregisterTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly GAP_REREGISTER_DEBOUNCE_MS = 250;
  // Events window: flipped when a
  // backward page comes back short.
  private eventsWindowExhausted = false;
  // Transcript window: loaded
  // Message rows — tail page + older pages + live-tail catch-up — ascending by
  // message_index. processEvents-derived state stays in `_state`; this lane is
  // the persisted-read window the UI binds.
  private transcript: Message[] = [];
  private transcriptById = new Map<string, Message>();
  private transcriptInitialized = false;
  private transcriptExhausted = false;
  private transcriptTailLoad: Promise<void> | null = null;
  private transcriptCatchUp: Promise<void> | null = null;
  private transcriptHandlers = new Set<() => void>();
  private _state: ProcessedState;
  private _connectionState: ConnectionState = 'disconnected';
  private _queue: QueuedMessage[] = [];
  private _queueVersion = -1;
  private _tasks: SessionTasks | null = null;
  private taskHandlers = new Set<(tasks: SessionTasks | null) => void>();
  private queuedImages = new QueuedImageStore(null);
  private resupplying = new Set<string>();
  private sendChain: Promise<unknown> = Promise.resolve();
  private _workflowId: string | null = null;
  private disconnected = false;
  private personaSeq = 0;

  private _bridgeUnsub: (() => Promise<void>) | null = null;
  private deregistration: Promise<void> = Promise.resolve();
  private _llmProgressUnsub: (() => void) | null = null;
  private _liveCalls = new Map<string, LiveLlmCall>();
  private _settledCallIds = new Set<string>();
  private _assistantCountAtSettle = new Map<string, number>();
  private liveCallHandlers = new Set<(calls: LiveLlmCall[]) => void>();
  private _eventStream: ShogunChopEventStream | null = null;
  private _eventStreamUnsub: (() => void) | null = null;

  private messageHandlers = new Set<MessageHandler>();
  private progressHandlers = new Set<ProgressHandler>();
  private stateHandlers = new Set<StateHandler>();
  private questionsHandlers = new Set<QuestionsHandler>();
  private connectionHandlers = new Set<ConnectionHandler>();
  private queueHandlers = new Set<QueueHandler>();
  private errorHandlers = new Set<ErrorHandler>();
  private contextHealthHandlers = new Set<ContextHealthHandler>();
  private participantHandlers = new Set<(participants: ChatParticipant[]) => void>();
  private participantSnapshot: Participant[] = [];
  private backgroundTaskSnapshot: RunningBackgroundTask[] = [];
  private participantSignature = '';
  private backgroundWorkHandlers = new Set<(items: BackgroundWorkItem[]) => void>();
  private backgroundWorkSignature = '';
  private participantCache: { state: ProcessedState; snapshot: Participant[]; list: ChatParticipant[] } | null = null;
  private participantRefresh: Promise<void> | null = null;
  private participantRefreshAgain = false;
  private participantWindows = new Map<string, { window: ParticipantTranscript; holds: number }>();
  private participantQueues = new Map<string, ParticipantQueueState>();
  private participantImageHolds = new Map<string, Set<string>>();
  private participantStatusEvents = new Map<string, { status: ParticipantStatus; seq: number }>();
  private participantSnapshotSeq = 0;
  private residentBackgroundStarts = new Map<number, EventSkeleton>();

  private prevMessageCount = 0;
  private prevState: ChatState = 'idle';
  private prevQuestionsSignature = '';
  private seededQuestions: ChatQuestion[] = [];

  constructor(
    sessionId: string | undefined,
    workspaceId: string | undefined,
    http: HttpLayer,
    tools: ToolsResource,
    deviceId: string | undefined,
    private historyCache: HistoryCache,
    windowPage: number = ChatSession.DEFAULT_WINDOW_PAGE,
    windowCeiling: number = deriveHistoryWindowCeiling(),
  ) {
    if (!Number.isInteger(windowPage) || windowPage < 1) {
      throw new Error(`historyPageSize must be a positive integer, got ${windowPage}`);
    }
    if (!Number.isInteger(windowCeiling) || windowCeiling < 1) {
      throw new Error(`historyWindowCeiling must be a positive integer, got ${windowCeiling}`);
    }
    this.windowPage = windowPage;
    this.windowCeiling = windowCeiling;
    this._sessionId = sessionId;
    this._workspaceId = workspaceId;
    this.http = http;
    this.deviceId = deviceId;
    this.tools = tools;
    this.skills = new SkillsResource(http);
    this.sessions = new SessionsResource(http);
    this.personas = new PersonaResource(http);
    this._state = processEvents([]);
  }

  private requireSessionId(): string {
    if (!this._sessionId) {
      throw new Error('ChatSession is still a draft');
    }
    return this._sessionId;
  }

  /** Injected by ChatResource so persist-on-first-action stays single-flight. */
  _setPersist(persist: (session: ChatSession) => Promise<void>): void {
    this.persistImpl = persist;
  }

  _acceptPersist(sessionId: string): void {
    this._sessionId = sessionId;
  }

  _notifyPersisted(): void {
    const id = this.requireSessionId();
    for (const handler of this.persistedHandlers) handler(id);
    this._loadTasks().catch((err: unknown) => this.reportError(err));
  }

  /** Settles once a disconnected handle's session-event deregistration has. */
  _deregistered(): Promise<void> {
    return this.deregistration;
  }

  /** Surface a failed connect/settings attempt on this handle (persist path). */
  _failConnection(message: string): void {
    for (const handler of this.errorHandlers) handler(message);
    // A failed attempt is 'error', never the pre-attempt 'disconnected'; a transport that reached 'connected' is not downgraded.
    if (this._connectionState !== 'connected') this.setConnectionState('error');
  }

  /** Create the backend row once; concurrent callers share the flight. */
  async ensurePersisted(): Promise<string> {
    if (this._sessionId) return this._sessionId;
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    if (this.inFlightPersist) return this.inFlightPersist;
    if (!this.persistImpl) {
      throw new Error('ChatSession has no persist implementation');
    }
    const persist = this.persistImpl;
    this.inFlightPersist = persist(this)
      .then(() => this.requireSessionId())
      .finally(() => {
        this.inFlightPersist = null;
      });
    return this.inFlightPersist;
  }

  private reasoningPrefix(): string {
    return this.workspaceId ? `/v1/reasoning/${this.workspaceId}` : `/v1/reasoning`;
  }

  get messages(): ChatMessage[] {
    const live = this._state.messages;
    const pending = this._optimistic.filter(
      (row) => !live.some((m) => m.role === 'user' && m.content === row.content),
    );
    return [...live, ...pending];
  }
  get state(): ChatState { return this._starting ? 'starting' : this._state.state; }
  get settings(): ChatSettings {
    return {
      personaId: this._personaId,
      processId: this._processOverride ?? this._processId,
      processInherited: this._processOverride === undefined,
      automaticProcessSelection:
        this._automaticOverride === undefined || this._automaticOverride === null
          ? this._automaticEffective
          : this._automaticOverride,
      automaticInherited: this._automaticOverride === undefined || this._automaticOverride === null,
      pinModelToTurn: this._pinModelToTurn,
      internalAgentVisibility: this._internalAgentVisibility,
      contextRagEnabled: this._contextRagEnabled,
      contextRagChildRunsEnabled: this._contextRagChildRunsEnabled,
      effectiveModel: this._effectiveModel,
    };
  }
  get progressTree(): ProgressTree | null { return this._state.progressTree; }
  get connectionState(): ConnectionState { return this._connectionState; }
  /** True once {@link disconnect} ran; a host holding this handle rebinds through `onLiveSessionChange`. */
  get isDisconnected(): boolean { return this.disconnected; }
  get queue(): QueuedMessage[] { return [...this._queue]; }

  /** The session's task list and Keep working state; null until read on connect, and for a draft. */
  get tasks(): SessionTasks | null { return this._tasks; }
  get reasoningRuns(): ProgressTree[] { return this._state.reasoningRuns; }
  get sessionEvents(): readonly EventSkeleton[] { return this.events; }
  /** Oldest loaded sequence_num; 0 when nothing is loaded. */
  get oldestLoadedSequence(): number {
    return this.events.length > 0 ? this.events[0].sequence_num : 0;
  }
  /** False once a backward page reached sequence 1 (session start). */
  get hasOlderEvents(): boolean {
    return !this.eventsWindowExhausted && this.oldestLoadedSequence > 1;
  }
  /** True while a detached history segment sits below released runs (sdk ADR-f43c7). */
  get hasNewerEvents(): boolean {
    return this.eventGap() !== null;
  }
  /** The runs released between the detached history segment and the live tail. */
  get historyGap(): HistoryGap | null {
    const gap = this.eventGap();
    if (!gap) return null;
    let below = 0;
    let above: EventSkeleton | undefined;
    for (const e of this.events) {
      if (e.event_type !== 'reasoning_started') continue;
      if (e.sequence_num <= gap.lo) below = Math.max(below, Number(e.data.run_ordinal));
      else if (e.sequence_num >= gap.hi) {
        above = e;
        break;
      }
    }
    if (!above) throw new Error(`history gap above sequence ${gap.lo} has no run start`);
    return {
      beforeRunId: String(above.data.run_id),
      fromOrdinal: below + 1,
      toOrdinal: Number(above.data.run_ordinal) - 1,
    };
  }
  /** Loaded transcript rows. */
  get transcriptMessages(): readonly Message[] { return this.transcript; }
  /** True while a detached transcript segment sits below released rows. */
  get hasNewerMessages(): boolean {
    return this.transcriptGap !== null;
  }
  get transcriptGap(): TranscriptGap | null {
    const gap = firstGap(this.transcript, (m) => m.message_index, Infinity);
    return gap ? { afterMessageIndex: gap.lo, beforeMessageIndex: gap.hi } : null;
  }
  /** False once a backward transcript page came back short. */
  get hasOlderMessages(): boolean {
    return !this.transcriptExhausted && this.transcript.length > 0;
  }
  /** Notified whenever the transcript window's contents change. */
  onTranscriptWindow(handler: () => void): () => void {
    this.transcriptHandlers.add(handler);
    return () => { this.transcriptHandlers.delete(handler); };
  }
  /** Highest gapless sequence_num — the replay cursor for BOTH reconnect and
   * gap re-register. Equals {@link maxSeenSequence} when contiguous. */
  get contiguousSequence(): number { return this.contiguousMax; }
  get maxSeenSequence(): number { return this.maxSequenceNum; }
  /** Cached event payload for a sequence number, if already fetched. */
  peekCachedPayload(seq: number): Record<string, unknown> | undefined {
    return this.payloadCache.get(seq);
  }
  get lastStopReason(): string | null { return this._state.lastStopReason; }
  /**
   * Open questions agents in this session asked, oldest first: the primary's,
   * subagents' and escalated ones, plus questions addressed to this session
   * from other sessions (sessions ADR-36b60). Answer one with {@link answerQuestion}.
   */
  get questions(): ChatQuestion[] {
    return [...this._state.questions.values()].filter((q) => q.status === 'open');
  }
  /** Answered and declined questions, oldest first, for the compact record of each. */
  get answeredQuestions(): ChatQuestion[] {
    return [...this._state.questions.values()].filter((q) => q.status === 'answered' || q.status === 'declined');
  }
  /** True while the primary's turn is held in `wait_for_answers` on a question it named that is open and addressed to the user (agents ADR-14bdc). */
  get waitingOnUser(): boolean { return this._state.waitingOnUser; }
  /**
   * Current pending destructive platform_call awaiting the user's approval, or
   * null (DESKTOP arm). Derived from `tool_call_result`
   * events; cleared when the re-issued call executes.
   */
  get pendingPlatformApproval(): PlatformApprovalRequest | null { return this._state.pendingPlatformApproval; }
  get pendingMediaActionApproval(): MediaActionApprovalRequest | null { return this._state.pendingMediaActionApproval; }
  /** A subagent participant's pending media-action approval, or null; answer it with {@link respondToMediaActionApproval} (agents ADR-055). */
  pendingMediaActionApprovalOf(participantId: string): MediaActionApprovalRequest | null {
    return this._state.participantMediaApprovals.get(participantId) ?? null;
  }
  /** Every subagent participant's pending media-action approval, with the participant that asked. */
  get participantMediaActionApprovals(): ReadonlyArray<{ participantId: string; request: MediaActionApprovalRequest }> {
    return [...this._state.participantMediaApprovals].map(([participantId, request]) => ({ participantId, request }));
  }
  get recentSteps(): ProgressStep[] { return this._state.progressTree?.steps ?? []; }

  /** The primary participant's id; null until the session snapshot or a run start names it. */
  get primaryParticipantId(): string | null {
    return this.snapshotPrimaryId() ?? this._state.primaryParticipantId;
  }

  /**
   * The session's participants, primary first: the session snapshot merged
   * with the lifecycle events of every subagent started since. A lifecycle
   * event newer than the snapshot read sets the status; the snapshot is
   * refetched only when an event names a participant it does not hold.
   */
  get participants(): ChatParticipant[] {
    const cached = this.participantCache;
    if (cached && cached.state === this._state && cached.snapshot === this.participantSnapshot) return cached.list;
    const primaryId = this.primaryParticipantId;
    const byId = new Map<string, ChatParticipant>();
    for (const p of this.snapshotParticipants()) byId.set(p.id, p);
    for (const [id, node] of this._state.participantNodes) {
      const known = byId.get(id);
      if (known) continue;
      const status = this._state.participantStatuses.get(id);
      if (status === undefined) throw new Error(`participant ${id} has a lifecycle node but no status`);
      const label = node.personaName || node.label;
      if (!label) throw new Error(`participant ${id} has no persona name or label`);
      byId.set(id, {
        id,
        isPrimary: false,
        label,
        agentId: node.agentId ?? null,
        personaName: node.personaName || null,
        ownerParticipantId: this._state.participantOwners.get(id) ?? null,
        spawnTaskId: node.parentId ?? null,
        background: node.background === true,
        status,
      });
    }
    const all = [...byId.values()];
    const list = [
      ...all.filter((p) => p.isPrimary || p.id === primaryId),
      ...all.filter((p) => !p.isPrimary && p.id !== primaryId),
    ];
    this.participantCache = { state: this._state, snapshot: this.participantSnapshot, list };
    return list;
  }

  /** The session's running work: every subagent, backgrounded tool call and background plan, running items oldest first, then finished items newest first. */
  get backgroundWork(): BackgroundWorkItem[] {
    return deriveBackgroundWork(this._state, this.participants, this.backgroundTaskSnapshot, this.events);
  }

  /** Notified when running work starts, ends or changes. */
  onBackgroundWorkChange(handler: (items: BackgroundWorkItem[]) => void): () => void {
    this.backgroundWorkHandlers.add(handler);
    return () => { this.backgroundWorkHandlers.delete(handler); };
  }

  /** Notified when a participant is added or changes status. */
  onParticipants(handler: (participants: ChatParticipant[]) => void): () => void {
    this.participantHandlers.add(handler);
    return () => { this.participantHandlers.delete(handler); };
  }

  /**
   * True while work this participant started is still live: a subagent it
   * started, transitively, that is running or idle, or a backgrounded tool
   * call or plan it or such a subagent started. This is what `stop({ mode: 'all' })`
   * on the participant would end (sessions ADR-2a41a). With `backgroundOnly`, a
   * foreground subagent does not count, since stopping the turn already ends it;
   * background work it started still does.
   */
  hasLiveWorkBelow(participantId: string, opts: { backgroundOnly?: boolean } = {}): boolean {
    const children = new Map<string, ChatParticipant[]>();
    for (const p of this.participants) {
      if (p.ownerParticipantId === null) continue;
      const list = children.get(p.ownerParticipantId) ?? [];
      list.push(p);
      children.set(p.ownerParticipantId, list);
    }
    const seen = new Set<string>();
    const stack = [participantId];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      if (this._state.liveBackgroundOwners.has(id)) return true;
      for (const child of children.get(id) ?? []) {
        if ((child.status === 'running' || child.status === 'idle') && (opts.backgroundOnly !== true || child.background)) return true;
        stack.push(child.id);
      }
    }
    return false;
  }

  /**
   * The windowed conversation of one subagent participant, read and cached by
   * `(session, participant)` and bounded like the chat transcript (sdk ADR-f43c7).
   * Each call takes a hold on the window; the view that called it calls
   * {@link releaseParticipantTranscript} when it closes, and the last release
   * frees the window. Call `loadTail()` to read it.
   */
  participantTranscript(participantId: string): ParticipantTranscript {
    if (participantId === this.primaryParticipantId) {
      throw new Error('the primary participant\'s conversation is the chat transcript (transcriptMessages)');
    }
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    const existing = this.participantWindows.get(participantId);
    if (existing) {
      existing.holds++;
      return existing.window;
    }
    const window = new ParticipantTranscript({
      sessions: this.sessions,
      http: this.http,
      historyCache: this.historyCache,
      workspaceId: this.workspaceId,
      sessionId: this.requireSessionId(),
      participantId,
      pageSize: this.windowPage,
      ceiling: this.windowCeiling,
      onError: (msg) => {
        for (const handler of this.errorHandlers) handler(msg);
      },
    });
    this.participantWindows.set(participantId, { window, holds: 1 });
    return window;
  }

  /** Releases one hold taken by {@link participantTranscript}; the last release frees the window. */
  releaseParticipantTranscript(participantId: string): void {
    const held = this.participantWindows.get(participantId);
    if (!held) throw new Error(`no hold on participant ${participantId}'s transcript`);
    held.holds--;
    if (held.holds > 0) return;
    held.window.dispose();
    this.participantWindows.delete(participantId);
  }

  private snapshotPrimaryId(): string | null {
    return this.participantSnapshot.find((p) => p.is_primary)?.id ?? null;
  }

  private fold(): void {
    for (const seq of this.residentBackgroundStarts.keys()) {
      if (this.skeletonBySeq.has(seq)) this.residentBackgroundStarts.delete(seq);
    }
    this._state = processEvents(this.events, {
      primaryParticipantId: this.snapshotPrimaryId(),
      participants: this.snapshotParticipants(),
      resident: [...this.residentBackgroundStarts.values()],
      backgroundTasks: this.backgroundTaskSnapshot,
      seedQuestions: this.seededQuestions,
    });
    this.seededQuestions = this.seededQuestions.filter((q) => this._state.questions.get(q.questionId)?.status === 'open');
    const detached = this._state.detachedNodes;
    for (const [seq, e] of this.residentBackgroundStarts) {
      if (detached.get(String(e.data.id))?.status !== 'running') this.residentBackgroundStarts.delete(seq);
    }
  }

  // (sdk ADR-f43c7)
  private holdLiveBackgroundStarts(next: readonly EventSkeleton[], kept: ReadonlySet<number>): void {
    const live = new Set<string>();
    for (const run of this._state.reasoningRuns) {
      for (const node of run.nodes) if (node.background === true && node.status === 'running') live.add(node.id);
    }
    for (const e of this.events) {
      if (kept.has(e.sequence_num) || e.event_type !== 'task_started') continue;
      if (e.data.background !== true || e.data.id === e.data.participant_id) continue;
      if (live.has(String(e.data.id))) this.residentBackgroundStarts.set(e.sequence_num, e);
    }
    if (next.length === 0) this.residentBackgroundStarts.clear();
  }

  /** The snapshot's participants, each with the status of any lifecycle event newer than the read. */
  private snapshotParticipants(): ChatParticipant[] {
    return this.participantSnapshot.map((row) => {
      const p = participantFromRow(row);
      const evented = this.participantStatusEvents.get(p.id);
      return evented && evented.seq > this.participantSnapshotSeq ? { ...p, status: evented.status } : p;
    });
  }

  // (sessions ADR-2a41a)
  private endsSnapshotBackgroundTask(batch: readonly EventSkeleton[]): boolean {
    if (this.isDraft || this.backgroundTaskSnapshot.length === 0) return false;
    return batch.some(
      (e) =>
        (e.event_type === 'task_completed' || e.event_type === 'task_failed') &&
        e.data.background === true &&
        e.data.id !== e.data.participant_id,
    );
  }

  // (sessions ADR-34ae2)
  private recordParticipantStatuses(batch: readonly EventSkeleton[]): boolean {
    if (this.isDraft) return false;
    const primaryId = this.primaryParticipantId;
    let unknown = false;
    for (const e of batch) {
      if (!PARTICIPANT_STATUS_EVENTS.has(e.event_type)) continue;
      const pid = e.data.participant_id;
      if (typeof pid !== 'string' || pid === primaryId) continue;
      if (e.event_type.startsWith('task_') && e.data.id !== pid) continue;
      const status = participantStatusFromEvent(e);
      const prior = this.participantStatusEvents.get(pid);
      if (!prior || prior.seq < e.sequence_num) this.participantStatusEvents.set(pid, { status, seq: e.sequence_num });
      if (!this.participantSnapshot.some((p) => p.id === pid)) unknown = true;
    }
    return unknown;
  }

  private catchUpParticipantTranscripts(batch: readonly EventSkeleton[]): void {
    for (const e of batch) {
      if (!PARTICIPANT_LANE_EVENTS.has(e.event_type)) continue;
      const pid = e.data.participant_id;
      if (typeof pid !== 'string') continue;
      this.participantWindows.get(pid)?.window.scheduleCatchUp();
    }
  }

  // (sdk ADR-b2a2e)
  private refreshParticipants(): void {
    if (this.participantRefresh) {
      this.participantRefreshAgain = true;
      return;
    }
    const readFrom = this.maxSequenceNum;
    this.participantRefresh = this.sessions
      .get(this._workspaceId, this.requireSessionId())
      .then((row) => {
        if (this.disconnected) return;
        if (!row.participants) throw new Error(`session ${row.id} read carries no participants`);
        if (!row.background_tasks) throw new Error(`session ${row.id} read carries no background_tasks`);
        this.participantSnapshot = row.participants;
        this.backgroundTaskSnapshot = row.background_tasks;
        this.participantSnapshotSeq = readFrom;
        this.fold();
        this.notifyChanges();
      })
      .catch((err: unknown) => this.reportError(err))
      .finally(() => {
        this.participantRefresh = null;
        if (this.participantRefreshAgain && !this.disconnected) {
          this.participantRefreshAgain = false;
          this.refreshParticipants();
        }
      });
  }

  private notifyParticipants(): void {
    if (this.participantHandlers.size === 0) return;
    const snapshot = this.participants;
    const signature = snapshot
      .map((p) => `${p.id}|${p.status}|${p.label}|${p.ownerParticipantId ?? ''}|${p.agentId ?? ''}|${p.background ? 1 : 0}|${this._state.participantMediaApprovals.get(p.id)?.approvalId ?? ''}`)
      .join('\n');
    if (signature === this.participantSignature) return;
    this.participantSignature = signature;
    for (const handler of this.participantHandlers) handler(snapshot);
  }

  /**
   * Send a message.
   *
   * `attachments` carries already-uploaded images as
   * `{ type: 'image', image_id, label }` — upload with
   * `sdk.sessions.uploadImage` first, so a failed upload surfaces at attach
   * time rather than aborting a send. They are MERGED with the file/directory
   * attachments derived from `mentions`, in mentions-then-images order, and all
   * of them precede the user text in the assembled turn.
   */
  send(
    content: string,
    mentions?: MentionRef[],
    viewContext?: ViewContext,
    attachments?: ImageAttachment[],
    options?: ChatSendOptions,
  ): void {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    void this.sendAsync(content, mentions, viewContext, attachments, options);
  }

  /**
   * Persist a draft if needed, then send. A send while a turn runs is held by
   * the session's server-side queue (sessions ADR-d28b1). Rejects on failure,
   * including `queue_full` and an image this device has not kept; a send from
   * an idle chat also rolls back its optimistic row.
   *
   * `options.participantId` sends to that subagent participant: the message
   * joins its conversation and starts or queues its turn, never the chat's.
   */
  async sendAsync(
    content: string,
    mentions?: MentionRef[],
    viewContext?: ViewContext,
    attachments?: ImageAttachment[],
    options?: ChatSendOptions,
  ): Promise<void> {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    const target = options?.participantId;
    if (target !== undefined && target !== this.primaryParticipantId) {
      await this.sendToParticipant(target, content, mentions, viewContext, attachments);
      return;
    }

    if (this._state.state === 'reasoning') {
      try {
        this.acceptSendResponse(await this.inSendOrder(() => this.postKept(content, mentions, viewContext, attachments)), attachments);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
        throw err;
      }
      return;
    }

    const optimistic = this.beginTurn(content);
    try {
      if (this.isDraft) {
        await this.ensurePersisted();
      }
      const res = await this.inSendOrder(() => this.postKept(content, mentions, viewContext, attachments));
      if (res.status === 'queued') {
        this._optimistic = this._optimistic.filter((row) => row.id !== optimistic.id);
        this._starting = false;
        for (const handler of this.stateHandlers) handler(this.state);
        for (const handler of this.messageHandlers) handler(optimistic);
      }
      this.acceptSendResponse(res, attachments);
    } catch (err) {
      this.failTurn(optimistic, err);
      throw err;
    }
  }

  private async sendToParticipant(
    participantId: string,
    content: string,
    mentions?: MentionRef[],
    viewContext?: ViewContext,
    attachments?: ImageAttachment[],
  ): Promise<void> {
    this.requireSessionId();
    try {
      const res = await this.inSendOrder(() =>
        this.postKept(content, mentions, viewContext, attachments, participantId),
      );
      if (res.status === 'queued' && attachments && attachments.length > 0) {
        const held = this.participantImageHolds.get(participantId) ?? new Set<string>();
        for (const a of attachments) held.add(a.image_id);
        this.participantImageHolds.set(participantId, held);
        this.trackParticipantQueue(participantId);
      }
      for (const a of attachments ?? []) this.queuedImages.settle(a.image_id);
      if (res.status === 'started' && attachments && attachments.length > 0) {
        this.queuedImages.release(this.requireSessionId(), this.keepQueuedImage()).catch((err: unknown) => this.reportError(err));
      }
      this.participantWindows.get(participantId)?.window.scheduleCatchUp();
      if (res.status === 'started') this.refreshParticipants();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      for (const handler of this.errorHandlers) handler(msg);
      throw err;
    }
  }

  // (sessions ADR-d28b1) One client's sends reach the server in the order the user made them.
  private inSendOrder<T>(post: () => Promise<T>): Promise<T> {
    const next = this.sendChain.then(post, post);
    this.sendChain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  // (sdk ADR-b0123)
  private async postKept(
    content: string,
    mentions?: MentionRef[],
    viewContext?: ViewContext,
    attachments?: ImageAttachment[],
    participantId?: string,
  ): Promise<SendMessageResponse> {
    await this.requireKeptImages(attachments);
    return this.postSend(content, mentions, viewContext, attachments, participantId);
  }

  _setQueuedImageStore(store: QueuedImageStore): void {
    this.queuedImages = store;
  }

  // (sdk ADR-b0123)
  private async requireKeptImages(attachments?: ImageAttachment[]): Promise<void> {
    if (!attachments || attachments.length === 0 || this.isDraft) return;
    const sessionId = this.requireSessionId();
    for (const image of attachments) {
      const refusal = (cause?: unknown) =>
        new QueuedImageStoreError(
          `This device can't keep a copy of ${image.label}, so the message can't be queued. ` +
            'Send it when the agent finishes, or remove the image.',
          cause,
        );
      let kept: Blob | null;
      try {
        kept = await this.queuedImages.get(sessionId, image.image_id);
      } catch (err) {
        throw refusal(err);
      }
      if (kept === null) throw refusal();
    }
  }

  private acceptSendResponse(res: SendMessageResponse, attachments?: ImageAttachment[]): void {
    if (res.status === 'started') {
      this._workflowId = res.workflow_id;
      if (attachments && attachments.length > 0 && !this.isDraft) {
        for (const a of attachments) this.queuedImages.settle(a.image_id);
        this.queuedImages.release(this.requireSessionId(), this.keepQueuedImage()).catch((err: unknown) => this.reportError(err));
      }
      return;
    }
    for (const a of attachments ?? []) this.queuedImages.settle(a.image_id);
    if (!this._queue.some((q) => q.id === res.queue_item.id)) {
      this._queue = [...this._queue, queuedMessageFromWire(res.queue_item)];
      this.notifyQueueChange();
    }
  }

  // --- Queue (sessions ADR-d28b1) ---

  /** A subagent participant's waiting user messages (sessions ADR-b016f); empty until {@link onParticipantQueue} has read them. */
  participantQueueOf(participantId: string): QueuedMessage[] {
    return [...(this.participantQueues.get(participantId)?.items ?? [])];
  }

  /**
   * Subscribe to a subagent participant's queue: reads it now, and again
   * whenever a `queue_updated` event names the participant.
   */
  onParticipantQueue(participantId: string, handler: QueueHandler): () => void {
    if (participantId === this.primaryParticipantId) {
      throw new Error('the primary participant\'s queue is the chat queue (onQueueChange)');
    }
    const entry = this.trackParticipantQueue(participantId);
    entry.handlers.add(handler);
    return () => {
      const current = this.participantQueues.get(participantId);
      if (!current) return;
      current.handlers.delete(handler);
      this.untrackIdleParticipantQueue(participantId);
    };
  }

  private trackParticipantQueue(participantId: string): ParticipantQueueState {
    let entry = this.participantQueues.get(participantId);
    if (!entry) {
      entry = { items: [], version: -1, handlers: new Set(), load: null, loadAgain: false };
      this.participantQueues.set(participantId, entry);
      this.loadParticipantQueue(participantId);
    }
    return entry;
  }

  // (sdk ADR-b0123)
  private untrackIdleParticipantQueue(participantId: string): void {
    const entry = this.participantQueues.get(participantId);
    if (!entry || entry.handlers.size > 0 || this.participantImageHolds.has(participantId)) return;
    this.participantQueues.delete(participantId);
  }

  private loadParticipantQueue(participantId: string): void {
    const entry = this.participantQueues.get(participantId);
    if (!entry || this.isDraft) return;
    if (entry.load) {
      entry.loadAgain = true;
      return;
    }
    entry.load = this.sessions
      .listQueue(this._workspaceId, this.requireSessionId(), { participantId })
      .then((snapshot) => this.applyParticipantQueue(participantId, snapshot))
      .catch((err: unknown) => this.reportError(err))
      .finally(() => {
        const current = this.participantQueues.get(participantId);
        if (!current) return;
        current.load = null;
        if (current.loadAgain) {
          current.loadAgain = false;
          this.loadParticipantQueue(participantId);
        }
      });
  }

  private applyParticipantQueue(participantId: string, snapshot: SessionQueue): void {
    const entry = this.participantQueues.get(participantId);
    if (!entry || snapshot.version < entry.version) return;
    entry.version = snapshot.version;
    entry.items = snapshot.items.map(queuedMessageFromWire);
    const items = [...entry.items];
    for (const handler of entry.handlers) handler(items);
    const held = this.participantImageHolds.get(participantId);
    if (!held) return;
    const queued = this.imageIdsOf(entry.items);
    for (const id of held) if (!queued.has(id)) held.delete(id);
    if (held.size === 0) this.participantImageHolds.delete(participantId);
    this.untrackIdleParticipantQueue(participantId);
    void this.reconcileQueuedImages().catch((err: unknown) => this.reportError(err));
  }

  private isPrimaryTarget(participantId: string | undefined): participantId is undefined {
    return participantId === undefined || participantId === this.primaryParticipantId;
  }

  /**
   * Seed the session's open questions to the user from the list route, so one
   * asked below the loaded history window still shows; its events keep it
   * current from then on (sessions ADR-c53f5).
   */
  async _loadOpenQuestions(): Promise<void> {
    if (this.isDraft) return;
    const sessionId = this.requireSessionId();
    const { questions } = await this.http.get<ListOpenQuestionsResponse>('/v1/reasoning/questions', { session_id: sessionId });
    this.seededQuestions = questions.map((q): ChatQuestion => ({
      questionId: q.question_id,
      askerParticipantId: q.asker_participant_id,
      askerName: q.asker_name,
      askerIsPrimary: q.asker_is_primary,
      askerSessionId: q.asker_session_id,
      addresseeKind: 'user',
      addresseeSessionId: q.session_id,
      addresseeParticipantId: null,
      escalatedFromParticipantId: q.escalated_from_participant_id,
      question: q.question,
      options: q.options,
      multiSelect: q.multi_select,
      default: q.default,
      status: 'open',
      answerOptionIndexes: [],
      answerText: null,
      answeredByKind: null,
      answeredById: null,
      cancelReason: null,
      askedAt: q.asked_at,
      resolvedAt: null,
    })).sort((a, b) => Date.parse(a.askedAt) - Date.parse(b.askedAt));
    this.fold();
    this.notifyChanges();
  }

  /** Read the server queue; called on connect and whenever `queue_updated` advances it. */
  async _loadQueue(): Promise<void> {
    if (this.isDraft) return;
    this.applyQueue(await this.sessions.listQueue(this._workspaceId, this.requireSessionId()));
  }

  /**
   * Replace a queued message's text; rejects with `queue_item_gone` once it started.
   * Without `mentions`, the item keeps its `@` attachments.
   */
  async editQueueItem(itemId: string, content: string, mentions?: MentionRef[], opts: { participantId?: string } = {}): Promise<void> {
    const target = opts.participantId;
    const kept = mentions ?? this.queuedMentions(itemId, target);
    const refs = kept.map((m) => ({ type: m.type, path: m.path, label: m.label }));
    const body = { content, ...(refs.length > 0 ? { attachments: refs } : {}) };
    if (this.isPrimaryTarget(target)) {
      this.applyQueue(await this.sessions.editQueueItem(this._workspaceId, this.requireSessionId(), itemId, body));
      return;
    }
    this.applyParticipantQueue(target, await this.sessions.editQueueItem(this._workspaceId, this.requireSessionId(), itemId, body, { participantId: target }));
  }

  private queuedMentions(itemId: string, participantId?: string): MentionRef[] {
    const queue = this.isPrimaryTarget(participantId) ? this._queue : (this.participantQueues.get(participantId)?.items ?? []);
    const item = queue.find((q) => q.id === itemId);
    if (!item) return [];
    const mentions: MentionRef[] = [];
    for (const a of item.attachments) {
      if (a.type !== 'image') mentions.push({ type: a.type, path: a.path, label: a.label });
    }
    return mentions;
  }

  /** Remove one queued message; rejects with `queue_item_gone` once it started. `participantId` targets a subagent's queue. */
  async removeQueueItem(itemId: string, opts: { participantId?: string } = {}): Promise<void> {
    const target = opts.participantId;
    if (this.isPrimaryTarget(target)) {
      this.applyQueue(await this.sessions.removeQueueItem(this._workspaceId, this.requireSessionId(), itemId));
      return;
    }
    this.applyParticipantQueue(target, await this.sessions.removeQueueItem(this._workspaceId, this.requireSessionId(), itemId, { participantId: target }));
  }

  /** Move a queued message one place; rejects with `queue_item_gone` once it started. `participantId` targets a subagent's queue. */
  async moveQueueItem(itemId: string, direction: 'up' | 'down', opts: { participantId?: string } = {}): Promise<void> {
    const target = opts.participantId;
    if (this.isPrimaryTarget(target)) {
      this.applyQueue(await this.sessions.moveQueueItem(this._workspaceId, this.requireSessionId(), itemId, direction));
      return;
    }
    this.applyParticipantQueue(target, await this.sessions.moveQueueItem(this._workspaceId, this.requireSessionId(), itemId, direction, { participantId: target }));
  }

  /** Remove every waiting message. `participantId` targets a subagent's queue. */
  async clearQueue(opts: { participantId?: string } = {}): Promise<void> {
    const target = opts.participantId;
    if (this.isPrimaryTarget(target)) {
      this.applyQueue(await this.sessions.clearQueue(this._workspaceId, this.requireSessionId()));
      return;
    }
    this.applyParticipantQueue(target, await this.sessions.clearQueue(this._workspaceId, this.requireSessionId(), { participantId: target }));
  }

  private applyQueue(snapshot: SessionQueue): void {
    if (snapshot.version < this._queueVersion) return;
    this._queueVersion = snapshot.version;
    this._queue = snapshot.items.map(queuedMessageFromWire);
    this.notifyQueueChange();
    void this.reconcileQueuedImages().catch((err: unknown) => this.reportError(err));
  }

  private imageIdsOf(items: readonly QueuedMessage[]): Set<string> {
    const ids = new Set<string>();
    for (const item of items) {
      for (const a of item.attachments) if (a.type === 'image') ids.add(a.image_id);
    }
    return ids;
  }

  // (sdk ADR-b0123)
  private keepQueuedImage(): (imageId: string) => boolean {
    const queued = this.imageIdsOf(this.everyQueuedItem());
    for (const held of this.participantImageHolds.values()) for (const id of held) queued.add(id);
    return (id) => queued.has(id);
  }

  private everyQueuedItem(): QueuedMessage[] {
    return [...this._queue, ...[...this.participantQueues.values()].flatMap((entry) => entry.items)];
  }

  // (sdk ADR-b0123, sessions ADR-d28b1)
  private async reconcileQueuedImages(): Promise<void> {
    if (this.isDraft) return;
    const sessionId = this.requireSessionId();
    await this.queuedImages.release(sessionId, this.keepQueuedImage());
    for (const item of this.everyQueuedItem()) {
      if (item.status !== 'blocked' || item.blockedReason !== 'image_expired') continue;
      for (const a of item.attachments) {
        if (a.type !== 'image' || this.resupplying.has(a.image_id)) continue;
        const blob = await this.queuedImages.get(sessionId, a.image_id);
        if (!blob) continue;
        this.resupplying.add(a.image_id);
        try {
          await this.sessions.uploadImage(sessionId, blob, a.label, this._workspaceId, 'upload', a.image_id);
        } finally {
          this.resupplying.delete(a.image_id);
        }
      }
    }
  }

  private reportError(err: unknown): void {
    const msg = err instanceof Error ? err.message : String(err);
    for (const handler of this.errorHandlers) handler(msg);
  }

  /** Load the account default persona onto a draft. */
  async seedDefaultPersona(): Promise<void> {
    const seq = ++this.personaSeq;
    // include_configs carries the config the draft needs, so this is ONE round
    // trip rather than a list plus a per-persona config read (persona ADR-021).
    const list = await this.personas.listPersonas({ include_configs: true });
    const seeded = list.find((p) => p.is_default);
    if (!seeded) {
      throw new Error('account has no default persona');
    }
    await this.applyDraftPersona(seq, seeded.id, seeded.latest_config);
  }

  // The latest persona call wins on a draft, whatever order the config reads resolve in (sdk ADR-042).
  private async applyDraftPersona(
    seq: number,
    personaId: string,
    known?: PersonaConfig | null,
  ): Promise<void> {
    const config = known ?? (await this.personas.getLatestConfig({ persona_id: personaId }));
    if (seq !== this.personaSeq) return;
    this.applyPersonaConfig(personaId, config);
    this._processOverride = undefined;
    this._automaticOverride = undefined;
    this.notifySettings();
  }

  /** Bind a persona and rematerialize process / automatic inherit. */
  async setPersona(personaId: string): Promise<void> {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    if (this.isDraft) {
      await this.applyDraftPersona(++this.personaSeq, personaId);
      return;
    }
    await this.sessions.update(this._workspaceId, this.requireSessionId(), {
      persona_id: personaId,
    });
    await this.loadSettings();
  }

  /** Pin an explicit process until the next setPersona. */
  async setProcess(agentProcessId: string): Promise<void> {
    if (this.isDraft) {
      this._processOverride = agentProcessId;
      this.notifySettings();
      return;
    }
    await this.sessions.update(this._workspaceId, this.requireSessionId(), {
      agent_process_id: agentProcessId,
    });
    await this.loadSettings();
    this._processOverride = agentProcessId;
    this.notifySettings();
  }

  /** Set or inherit (`null`) automatic process selection. */
  async setAutomaticProcessSelection(value: boolean | null): Promise<void> {
    if (this.isDraft) {
      this._automaticOverride = value === null ? undefined : value;
      this.notifySettings();
      return;
    }
    await this.sessions.update(this._workspaceId, this.requireSessionId(), {
      automatic_process_selection: value,
    });
    await this.loadSettings();
  }

  /** Pin the session model to every execution in the turn. */
  async setPinModelToTurn(pin: boolean): Promise<void> {
    if (this.isDraft) {
      this._pinModelToTurn = pin;
      this.notifySettings();
      return;
    }
    await this.sessions.update(this._workspaceId, this.requireSessionId(), {
      pin_model_to_turn: pin,
    });
    await this.loadSettings();
  }

  /** Toggle run-start context retrieval for this chat. */
  async setContextRagEnabled(enabled: boolean): Promise<void> {
    if (this.isDraft) {
      this._contextRagEnabled = enabled;
      this.notifySettings();
      return;
    }
    const prevRoot = this._contextRagEnabled;
    this._contextRagEnabled = enabled;
    this.notifySettings();
    try {
      await this.sessions.update(this._workspaceId, this.requireSessionId(), {
        context_rag_enabled: enabled,
      });
      await this.loadSettings();
    } catch (err) {
      this._contextRagEnabled = prevRoot;
      this.notifySettings();
      throw err;
    }
  }

  /** Toggle context retrieval for sub-agent runs in this chat. */
  async setContextRagChildRunsEnabled(enabled: boolean): Promise<void> {
    if (this.isDraft) {
      this._contextRagChildRunsEnabled = enabled;
      this.notifySettings();
      return;
    }
    const prevChild = this._contextRagChildRunsEnabled;
    this._contextRagChildRunsEnabled = enabled;
    this.notifySettings();
    try {
      await this.sessions.update(this._workspaceId, this.requireSessionId(), {
        context_rag_child_runs_enabled: enabled,
      });
      await this.loadSettings();
    } catch (err) {
      this._contextRagChildRunsEnabled = prevChild;
      this.notifySettings();
      throw err;
    }
  }

  /** Set internal-agent visibility for this chat. */
  async setInternalAgentVisibility(visibility: InternalAgentVisibility): Promise<void> {
    if (this.isDraft) {
      this._internalAgentVisibility = visibility;
      this.notifySettings();
      return;
    }
    await this.sessions.update(this._workspaceId, this.requireSessionId(), {
      internal_agent_visibility: visibility,
    });
    await this.loadSettings();
  }

  /** Attach a workspace; persists immediately on a draft. */
  async attachWorkspace(workspaceId: string): Promise<void> {
    this._workspaceId = workspaceId;
    await this.ensurePersisted();
  }

  /** Drop a draft workspace before persist. */
  clearWorkspace(): void {
    if (!this.isDraft) {
      throw new Error('workspace is fixed after persist');
    }
    this._workspaceId = undefined;
  }

  /** Replace settings from the persisted session row. */
  async loadSettings(): Promise<void> {
    const row = await this.sessions.get(this._workspaceId, this.requireSessionId());
    this.applySessionRecord(row);
    this.notifySettings();
  }

  /** Subscribe to the first persist of a draft. */
  onPersisted(handler: PersistedHandler): () => void {
    this.persistedHandlers.add(handler);
    return () => { this.persistedHandlers.delete(handler); };
  }

  /** Subscribe to settings projection changes. */
  onSettings(handler: SettingsHandler): () => void {
    this.settingsHandlers.add(handler);
    return () => { this.settingsHandlers.delete(handler); };
  }

  private applyPersonaConfig(personaId: string, config: PersonaConfig): void {
    const agent = JSON.parse(config.agent_config_json) as PersonaAgentConfig;
    this._personaId = personaId;
    this._processId = agent.agent_process_id;
    this._automaticEffective = agent.automatic_process_selection;
    this._effectiveModel = {
      providerType: agent.provider_type,
      modelName: agent.model_name,
      source: 'persona',
    };
  }

  private applySessionRecord(row: Session): void {
    if (row.participants) {
      if (!row.background_tasks) throw new Error(`session ${row.id} read carries no background_tasks`);
      this.participantSnapshot = row.participants;
      this.backgroundTaskSnapshot = row.background_tasks;
      this.participantSnapshotSeq = this.maxSequenceNum;
      this.fold();
      this.notifyChanges();
    }
    this._personaId = row.persona_id ?? undefined;
    this._processId = row.agent_process_id;
    this._processOverride = undefined;
    this._automaticOverride = row.automatic_process_selection_override;
    this._automaticEffective = row.effective_automatic_process_selection;
    this._pinModelToTurn = row.pin_model_to_turn;
    this._internalAgentVisibility = row.internal_agent_visibility;
    this._contextRagEnabled = row.context_rag_enabled ?? undefined;
    this._contextRagChildRunsEnabled = row.context_rag_child_runs_enabled ?? undefined;
    const eac = row.effective_agent_config;
    if (eac && 'error' in eac) {
      this._effectiveModel = { error: eac.error };
    } else if (eac) {
      this._effectiveModel = {
        providerType: eac.provider_type,
        modelName: eac.model_name,
        source: eac.source,
      };
    }
  }

  private notifySettings(): void {
    const snapshot = this.settings;
    for (const handler of this.settingsHandlers) handler(snapshot);
  }

  private beginTurn(content: string): ChatMessage {
    const optimistic: ChatMessage = {
      id: `pending-${uuidV4()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    this._optimistic.push(optimistic);
    this._starting = true;
    for (const handler of this.messageHandlers) handler(optimistic);
    for (const handler of this.stateHandlers) handler('starting');
    return optimistic;
  }

  private failTurn(optimistic: ChatMessage, err: unknown): void {
    this._optimistic = this._optimistic.filter((row) => row.id !== optimistic.id);
    this._starting = false;
    const msg = err instanceof Error ? err.message : String(err);
    for (const handler of this.errorHandlers) handler(msg);
    for (const handler of this.stateHandlers) handler(this.state);
    for (const handler of this.messageHandlers) handler(optimistic);
  }

  // --- Capabilities ---

  /**
   * List the capabilities available in this session's workspace.
   *
   * Merges TWO discovery calls:
   *   - `sdk.tools.listAvailableTools(workspaceId, deviceId)` → `kind:"coded"`
   *     (device id threaded so device-routed local tools are listed).
   *   - workspace: `GET /v1/tools/{workspaceId}/skills` (merged workspace + catalog), never SDK-retried
   *   - workspace-less: `sdk.skills.list()` (catalog only; no workstation wake)
   *
   * `kind` is set purely by which call produced the entry. Neither call is
   * wrapped in a fallback: if either discovery surface fails, the whole listing
   * rejects (no-fallbacks).
   */
  async listCapabilities(): Promise<Capability[]> {
    const toolsPromise = this.tools.listAvailableTools(
      this.workspaceId,
      this.deviceId,
    );
    // (bridge ADR-001)
    const skillsPromise = this.workspaceId
      ? this.http.get<ListAvailableSkillsResponse>(
          `/v1/tools/${this.workspaceId}/skills`,
          { device_id: this.deviceId },
          { retry: false },
        )
      : this.skills.list();

    const [toolsRes, skillsRes] = await Promise.all([toolsPromise, skillsPromise]);

    const coded: Capability[] = toolsRes.tools.map((t) => {
      const cap: Capability = {
        name: t.name,
        description: t.description,
        kind: 'coded',
        parameters: t.parameters,
      };
      if (t.mcp_id) {
        cap.mcpId = t.mcp_id;
      }
      return cap;
    });

    const skills: Capability[] = this.workspaceId
      ? (skillsRes as { skills: Array<{
          name: string;
          summary: string;
          framework?: string;
          qualified_name?: string;
          shadowed_by?: string;
          namespace?: string;
        }> }).skills.map((s) => ({
          name: s.name,
          description: s.summary,
          kind: 'skill' as const,
          framework: s.framework,
          qualifiedName: s.qualified_name,
          shadowedBy: s.shadowed_by,
          namespace: s.namespace,
        }))
      : (skillsRes as { skills: Array<{ name: string; description: string }> }).skills.map((s) => ({
          name: s.name,
          description: s.description,
          kind: 'skill' as const,
          namespace: 'shogun',
          qualifiedName: `shogun::${s.name}`,
        }));

    return [...coded, ...skills];
  }

  /** Decide how the composer should capture arguments for a capability. */
  planArgCapture(cap: Capability): ArgCapture {
    const required = requiredParams(cap.parameters);
    if (required.length === 0) {
      return { mode: 'none' };
    }
    if (required.length === 1) {
      return { mode: 'tail', param: required[0] };
    }
    const props = (cap.parameters?.properties ?? {}) as Record<
      string,
      Record<string, unknown>
    >;
    const fields: ArgField[] = required.map((name) => {
      const spec = props[name] ?? {};
      const field: ArgField = {
        name,
        type: typeof spec.type === 'string' ? spec.type : 'string',
        required: true,
      };
      if (typeof spec.description === 'string') {
        field.description = spec.description;
      }
      return field;
    });
    return { mode: 'form', fields };
  }

  /**
   * Invoke a capability. Accepts the selected `Capability` (the palette always
   * holds it) or an unambiguous name string.
   *
   * For a **coded** capability: generates a client `invocation_id` and makes
   * ONE call to `POST /v1/reasoning/{ws}/invoke-capability`, which executes the
   * tool server-side and persists the call+result pair. Returns
   * `{ kind:"coded", status, entryRef?, preview?, errorKind? }`. A 409
   * (session busy) rejection is surfaced as a typed `SessionBusyError`.
   *
   * **`workspace_init` / `/init`:** still a coded capability on this
   * path, but its executor **auto-starts** `Agent.Execute` with the seeded
   * `workspace-initializer` system persona in the current session (exception to
   * seed-don't-auto-start). The coded outcome's preview is the
   * `WorkspaceInitAgentResult` JSON envelope (`persona`, `run_id`, `session_id`,
   * `started`). Pass `{ instructions }` (at most 64 KiB) to put the user's
   * request in the initializer's kickoff message. Follow-up user messages are
   * normal `send()` turns.
   *
   * A **skill** capability is not invocable. Send the name as text and the
   * assistant reads it with `skill_read`.
   *
   * A bare-name string that matches more than one capability (a coded tool and
   * a skill may share a name) throws `AmbiguousCapabilityError` —
   * pass the selected `Capability` object to dispatch by its `kind`.
   */
  async invoke(
    cap: Capability | string,
    args: Record<string, unknown> = {},
  ): Promise<InvokeOutcome> {
    if (this.disconnected) throw new SDKError('ChatSession is disconnected');

    // Defensive guard: a workspace-scoped capability cannot run on a
    // workspace-less session. Reject synchronously BEFORE any network call
    // (including capability resolution, which itself fetches). In practice such
    // a capability is never in a workspace-less session's discovery set, so
    // this only fires when a caller invokes a workspace tool by name/object on
    // a session that has no workspace.
    if (this.workspaceId === undefined && this.isWorkspaceScopedCapability(cap)) {
      const name = typeof cap === 'string' ? cap : cap.name;
      throw new WorkspaceRequiredError(name);
    }

    const resolved =
      typeof cap === 'string' ? await this.resolveCapabilityByName(cap) : cap;

    if (resolved.kind === 'skill') {
      throw new SDKError(
        `skill "${resolved.name}" is not invocable; send it as text and the assistant reads it with skill_read`,
      );
    }

    const invocationId = uuidV4();
    const body: Record<string, unknown> = {
      invocation_id: invocationId,
      session_id: this.requireSessionId(),
      tool_name: resolved.name,
      arguments: args,
    };
    if (this.deviceId) {
      body.device_id = this.deviceId;
    }
    if (resolved.mcpId) {
      body.mcp_id = resolved.mcpId;
    }

    let res: InvokeCapabilityWireResponse;
    try {
      res = await this.http.post<InvokeCapabilityWireResponse>(
        `${this.reasoningPrefix()}/invoke-capability`,
        body,
      );
    } catch (err) {
      if (isConflict(err)) {
        throw new SessionBusyError(
          this.requireSessionId(),
          err instanceof Error ? err.message : undefined,
        );
      }
      throw err;
    }
    const outcome = codedOutcomeFromWire(res);
    if (resolved.name === 'workspace_init' && outcome.kind === 'coded' && !outcome.errorKind) {
      assertWorkspaceInitAgentStarted(outcome.preview);
    }
    return outcome;
  }

  private async resolveCapabilityByName(name: string): Promise<Capability> {
    const caps = await this.listCapabilities();
    const matches = caps.filter((c) => c.name === name);
    if (matches.length === 0) {
      const scope = this.workspaceId
        ? `workspace ${this.workspaceId}`
        : 'this workspace-less session';
      throw new SDKError(`No capability named "${name}" is available in ${scope}`);
    }
    if (matches.length > 1) {
      throw new AmbiguousCapabilityError(name, matches);
    }
    return matches[0];
  }

  private isWorkspaceScopedCapability(cap: Capability | string): boolean {
    const name = typeof cap === 'string' ? cap : cap.name;
    if (CATALOG_SKILL_TOOL_NAMES.has(name)) {
      return false;
    }
    if (typeof cap === 'string') {
      return WORKSPACE_SCOPED_TOOL_NAMES.has(cap);
    }
    if (cap.mcpId && WORKSPACE_SCOPED_MCP_IDS.has(cap.mcpId)) {
      return true;
    }
    return WORKSPACE_SCOPED_TOOL_NAMES.has(cap.name);
  }

  /** Answer a pending media-action approval, the primary's or a subagent participant's, by its approval id. */
  respondToMediaActionApproval(response: MediaActionApprovalResponse): void {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    const subagent = [...this._state.participantMediaApprovals.values()].find((r) => r.approvalId === response.approvalId);
    if (subagent && !subagent.workflowId) {
      throw new Error(`media action approval ${response.approvalId} names no workflow to answer`);
    }
    const workflowId =
      subagent?.workflowId ?? this._state.pendingMediaActionApproval?.workflowId ?? this._workflowId;
    if (!workflowId) {
      const msg =
        'Cannot approve media action: no in-flight workflow_id on this ChatSession';
      for (const handler of this.errorHandlers) handler(msg);
      throw new Error(msg);
    }
    void this.http
      .post(`${this.reasoningPrefix()}/sessions/${this.requireSessionId()}/media-action-approval`, {
        workflow_id: workflowId,
        approval_id: response.approvalId,
        approved: response.approved,
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      });
  }

  /**
   * Answer an open question in this session: choose options, write text, or
   * both; or pass `{ decline: true }` to let the agent use its judgment. The
   * first answer wins; a rejected answer rejects the promise and reaches
   * {@link onError} (sessions ADR-36b60).
   */
  async answerQuestion(questionId: string, answer: QuestionAnswer): Promise<AnsweredQuestion> {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    const body = answer.decline
      ? { decline: true }
      : {
          ...(answer.optionIndexes && answer.optionIndexes.length > 0 ? { option_indexes: answer.optionIndexes } : {}),
          ...(answer.text !== undefined ? { text: answer.text } : {}),
        };
    try {
      return await this.http.post<AnsweredQuestion>(
        `${this.reasoningPrefix()}/sessions/${this.requireSessionId()}/questions/${encodeURIComponent(questionId)}/answer`,
        body,
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      for (const handler of this.errorHandlers) handler(msg);
      throw err;
    }
  }

  /**
   * Edit a message and rewind the session to it: on success the transcript
   * window drops pages at/above the rewind point and refetches the tail — one
   * owner for the rewind + window reset (adr.md ADR-006).
   */
  async editMessageAndRewind(messageId: string, content: string): Promise<Message[]> {
    const sessionId = this.requireSessionId();
    const result = await this.sessions.editMessageAndRewind(
      this.workspaceId,
      sessionId,
      messageId,
      content,
    );
    const rewindIdx = this.transcriptById.get(messageId)?.message_index;
    const kept =
      rewindIdx !== undefined
        ? this.transcript.filter((m) => m.message_index < rewindIdx)
        : [];
    this.transcript = kept;
    this.transcriptById.clear();
    for (const m of kept) this.transcriptById.set(m.id, m);
    this.transcriptExhausted = false;
    // The rewind point's window rows and the matching history-cache pages are
    // gone server-side — drop them in the same owner.
    const identity = await this.http.actingAccountId();
    // A rewind point outside the loaded window drops the whole entry (index 0).
    this.historyCache.dropAtOrAbove(identity, this.workspaceId, sessionId, rewindIdx ?? 0);
    if (this.transcriptInitialized) await this.fetchTranscriptTail();
    this.notifyTranscriptChanged();
    return result;
  }

  /**
   * Stop work in this session. `mode: 'turn'` ends the participant's turn and
   * its foreground work and spares background work; the default `all` stops the
   * participant's whole subtree. The participant defaults to the primary.
   */
  stop(opts: { mode?: StopMode; participantId?: string } = {}): void {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    const forPrimary = opts.participantId === undefined || opts.participantId === this.primaryParticipantId;
    void this.http
      .post(`${this.reasoningPrefix()}/sessions/${this.requireSessionId()}/stop`, {
        ...(forPrimary && this._workflowId ? { workflow_id: this._workflowId } : {}),
        ...(opts.mode ? { mode: opts.mode } : {}),
        ...(opts.participantId ? { participant_id: opts.participantId } : {}),
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      });
  }

  /**
   * Redirect a turn with new user content. The primary's redirect needs its
   * in-flight workflow id; a subagent participant's needs only its id.
   */
  redirect(content: string, opts: { participantId?: string } = {}): void {
    if (this.disconnected) throw new Error('ChatSession is disconnected');
    const subagent = opts.participantId !== undefined && opts.participantId !== this.primaryParticipantId;
    if (!subagent && !this._workflowId) {
      const msg =
        'Cannot redirect: no in-flight workflow_id on this ChatSession (send a message first, or wait for reasoning_started)';
      for (const handler of this.errorHandlers) handler(msg);
      throw new Error(msg);
    }
    void this.http
      .post(`${this.reasoningPrefix()}/sessions/${this.requireSessionId()}/redirect`, {
        ...(subagent ? { participant_id: opts.participantId } : { workflow_id: this._workflowId }),
        content,
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      });
  }

  /** Subscribe to new chat messages. */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => { this.messageHandlers.delete(handler); };
  }

  /**
   * Text that still-running LLM calls have streamed so far, one entry per call
   * (agents ADR-01674). Presentation only: never persisted, empty after a
   * reconnect until the next frame, and an entry leaves once its call's
   * persisted completion is folded.
   */
  get liveLlmCalls(): LiveLlmCall[] {
    return [...this._liveCalls.values()];
  }

  /** Subscribe to changes in {@link liveLlmCalls}. */
  onLiveLlmCalls(handler: (calls: LiveLlmCall[]) => void): () => void {
    this.liveCallHandlers.add(handler);
    return () => { this.liveCallHandlers.delete(handler); };
  }

  /** Subscribe to progress-tree updates. */
  onProgressUpdate(handler: ProgressHandler): () => void {
    this.progressHandlers.add(handler);
    return () => { this.progressHandlers.delete(handler); };
  }

  /** Subscribe to chat state changes. */
  onStateChange(handler: StateHandler): () => void {
    this.stateHandlers.add(handler);
    return () => { this.stateHandlers.delete(handler); };
  }

  /** Subscribe to changes in {@link questions}, {@link answeredQuestions} and {@link waitingOnUser}. */
  onQuestionsChange(handler: QuestionsHandler): () => void {
    this.questionsHandlers.add(handler);
    return () => { this.questionsHandlers.delete(handler); };
  }

  /** Subscribe to connection-state changes. */
  onConnectionStateChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => { this.connectionHandlers.delete(handler); };
  }

  /** Subscribe to the session's task list: called on every newer version, live from `tasks_updated`. */
  onTasksChange(handler: (tasks: SessionTasks | null) => void): () => void {
    this.taskHandlers.add(handler);
    return () => { this.taskHandlers.delete(handler); };
  }

  /** Read the task list; called on connect and when a `tasks_updated` event carries a newer version. */
  async _loadTasks(): Promise<void> {
    if (this.isDraft) return;
    this.applyTasks(await this.sessions.listTasks(this._workspaceId, this.requireSessionId()));
  }

  /** Turn the session's Keep working on or off and apply the returned task snapshot. */
  async setKeepWorking(on: boolean): Promise<SessionTasks> {
    const snapshot = await this.sessions.setKeepWorking(this._workspaceId, this.requireSessionId(), on);
    this.applyTasks(snapshot);
    return snapshot;
  }

  private applyTasks(snapshot: SessionTasks): void {
    if (this._tasks !== null && snapshot.version <= this._tasks.version) return;
    this._tasks = snapshot;
    for (const handler of this.taskHandlers) handler(snapshot);
  }

  /** Subscribe to outbound queue changes. */
  onQueueChange(handler: QueueHandler): () => void {
    this.queueHandlers.add(handler);
    return () => { this.queueHandlers.delete(handler); };
  }

  /** Subscribe to session errors. */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => { this.errorHandlers.delete(handler); };
  }

  /**
   * Live `context_health` session events.
   * Fires when a turn's per-source health arrives over the bridge demux so the
   * Context tab can refresh without a dry-run assemble.
   */
  /** Subscribe to context-health updates. */
  onContextHealth(handler: ContextHealthHandler): () => void {
    this.contextHealthHandlers.add(handler);
    return () => { this.contextHealthHandlers.delete(handler); };
  }

  /** Disconnect this handle from the live stream. */
  disconnect(): void {
    this.disconnected = true;
    for (const { window } of this.participantWindows.values()) window.dispose();
    this.participantWindows.clear();
    this.participantQueues.clear();
    if (this.gapReregisterTimer !== null) {
      clearTimeout(this.gapReregisterTimer);
      this.gapReregisterTimer = null;
    }
    this.sessionSource = null;
    if (this._bridgeUnsub) {
      this.deregistration = this._bridgeUnsub();
      this._bridgeUnsub = null;
    }
    if (this._llmProgressUnsub) {
      this._llmProgressUnsub();
      this._llmProgressUnsub = null;
    }
    this._liveCalls.clear();
    if (this._eventStreamUnsub) {
      this._eventStreamUnsub();
      this._eventStreamUnsub = null;
    }
    if (this._eventStream) {
      this._eventStream.close();
      this._eventStream = null;
    }
    this.setConnectionState('disconnected');
  }

  /**
   * Wire the session's real-time event transport.
   *
   *   - WEB (`sessionEvents` present, no daemon): load the same run-aligned REST
   *     tail window as desktop, then register on the shared per-tab cloud
   *     `BridgeEventStream`. `registerSession` opens the HTTP subscription
   *     (which replays persisted events after `contiguousMax`, the tail's high
   *     water mark, so replay is gap-fill only, sessions ADR-7ae5d) and
   *     returns the initial-subscribe promise. We AWAIT it so a 403/404/network
   *     failure is observable (`connectionState('error')` + error handlers),
   *     never a silent connected-but-deaf chat (no-fallbacks). We do NOT
   *     set `'connected'` on the failure path.
   *   - DESKTOP (`shogunchopResource` + `webSocketFactory` present): live push
   *     via daemon `/events` (tradestudy Decision 1, Option A). The daemon ring
   *     does NOT replay `session.event` history, so we AWAIT a one-shot REST
   *     catch-up via {@link SessionsResource.getEvents} (paged) before marking
   *     connected — same reconstructibility of Agent activity / Progress that
   *     web gets from subscribe replay. Live frames that race the catch-up are
   *     deduped by `sequence_num` in {@link addEvents}.
   */
  async _initialize(
    sessionEvents: BridgeSessionSource | null,
    shogunchopResource: ShogunChopResource | null,
    webSocketFactory?: WebSocketFactory,
  ): Promise<void> {
    if (shogunchopResource && webSocketFactory) {
      this._eventStream = shogunchopResource.events(webSocketFactory);
      this._eventStreamUnsub = this._eventStream.on((event) => {
        if (event.event === 'session.event') {
          const data = event.data as Record<string, unknown>;
          if (data?.session_id === this._sessionId) {
            this._addEventFromPush(data);
          }
        } else if (event.event === 'agent.llmProgress') {
          if (event.data.session_id === this._sessionId) {
            this._applyLlmProgress(event.data);
          }
        }
      });
      try {
        await this.backfillTailWindowFromRest();
        this.setConnectionState('connected');
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
        this.setConnectionState('error');
      }
      return;
    }

    if (sessionEvents) {
      this.sessionSource = sessionEvents;
      try {
        await this.backfillTailWindowFromRest();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
        this.setConnectionState('error');
        return;
      }
      const { registered, unsubscribe } = sessionEvents.registerSession(
        this.requireSessionId(),
        (data) => this._addEventFromPush(data),
        // Latent-bug fix: the replay cursor is contiguousMax (highest
        // GAPLESS seq), NOT maxSequenceNum (highest SEEN). A reconnect after a
        // sub-max hole must redeliver from the last contiguous event to refill
        // the hole; maxSequenceNum would skip it forever.
        () => this.contiguousMax,
        this.workspaceId,
      );
      this._bridgeUnsub = unsubscribe;
      this._llmProgressUnsub = sessionEvents.onLlmProgress(this.requireSessionId(), (frame) =>
        this._applyLlmProgress(frame),
      );
      try {
        // AWAIT the initial registration: a 403/404/network failure must
        // be observable, never a silent connected-but-deaf chat (no-fallbacks).
        await registered;
        this.setConnectionState('connected');
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
        this.setConnectionState('error'); // NOT 'connected'
      }
      return;
    }

    // Neither transport is wired — on web the app has not attached the shared
    // BridgeSessionSource yet, on desktop the daemon arm is unconfigured. Fail
    // loudly rather than leaving a silently-deaf session that renders a ready
    // composer whose first send throws (no-fallbacks).
    const msg =
      'no session-event transport wired: bridge session source not attached and desktop daemon arm (shogunchopResource + webSocketFactory) not configured';
    for (const handler of this.errorHandlers) handler(msg);
    this.setConnectionState('error');
  }

  /**
   * Initial history on both arms: the newest page of skeleton events, extended
   * backward to a run boundary (sessions ADR-7ae5d).
   */
  private async backfillTailWindowFromRest(): Promise<void> {
    await this.loadRunAligned({ latest: true }, this.windowPage);
    // The loaded window is internally gapless and the un-loaded head below it
    // is not a live gap: seed both watermarks to the window max.
    const windowMax =
      this.events.length > 0 ? this.events[this.events.length - 1].sequence_num : 0;
    this.maxSequenceNum = Math.max(this.maxSequenceNum, windowMax);
    this.contiguousMax = Math.max(this.contiguousMax, windowMax);
  }

  private async fetchEventsPage(
    mode: { latest: true } | { beforeSequence: number },
    limit: number,
  ): Promise<SessionEvent[]> {
    const page = await this.sessions.getEvents(
      this.workspaceId,
      this.requireSessionId(),
      undefined,
      limit,
      'latest' in mode
        ? { latest: true, skeleton: true }
        : { beforeSequence: mode.beforeSequence, skeleton: true },
    );
    if (page.length < limit) this.eventsWindowExhausted = true;
    return page;
  }

  // (sdk ADR-78ebf)
  private async loadRunAligned(
    mode: { latest: true } | { beforeSequence: number },
    limit: number,
  ): Promise<SessionEvent[]> {
    const collected = await this.fetchEventsPage(mode, limit);
    for (;;) {
      if (collected.length === 0 || this.eventsWindowExhausted || collected[0].sequence_num <= 1) break;
      const firstStart = collected.findIndex((e) => e.event_type === 'reasoning_started');
      if (firstStart >= 0) {
        collected.splice(0, firstStart);
        break;
      }
      collected.unshift(
        ...(await this.fetchEventsPage({ beforeSequence: collected[0].sequence_num }, limit)),
      );
    }
    this.addEvents(collected.map((ev) => this.restSkeleton(ev)));
    return collected;
  }

  private restSkeleton(ev: SessionEvent): EventSkeleton {
    // Skeleton rows are bridge-frame shaped (skeletonFrameElement
    // carries no id/session_id); derive them exactly like the push path does.
    return {
      id: ev.id ?? `rest-${ev.sequence_num}`,
      session_id: ev.session_id ?? this.requireSessionId(),
      event_type: ev.event_type,
      role: ev.role ?? 'system',
      sequence_num: ev.sequence_num,
      size_bytes: ev.size_bytes ?? 0,
      data: ev.data ?? {},
      handle: ev.handle,
      hydrated: ev.handle === undefined,
      created_at: ev.created_at,
    };
  }

  /**
   * Backward events page below {@link oldestLoadedSequence}, aligned so it
   * begins at a run start: no loaded run is ever missing its start. Empty
   * result = session start. Concurrent callers share the in-flight page.
   */
  loadOlderEvents(limit?: number): Promise<readonly SessionEvent[]> {
    this.inFlightOlderEvents ??= this.fetchOlderEvents(limit ?? this.windowPage).finally(() => {
      this.inFlightOlderEvents = null;
    });
    return this.inFlightOlderEvents;
  }

  private async fetchOlderEvents(pageLimit: number): Promise<readonly SessionEvent[]> {
    if (!this.hasOlderEvents) return [];
    const page = await this.loadRunAligned({ beforeSequence: this.oldestLoadedSequence }, pageLimit);
    this.replaceEvents(capEvents(this.events, this.windowCeiling, 'newest'));
    return page;
  }

  /**
   * Forward events page that refills the released span above the detached
   * history segment, ending on a run boundary or where it meets the loaded
   * runs above. Empty result = not detached.
   */
  async loadNewerEvents(): Promise<readonly SessionEvent[]> {
    const gap = this.eventGap();
    if (!gap) return [];
    const collected: SessionEvent[] = [];
    let cursor = gap.lo;
    for (;;) {
      const page = await this.sessions.getEvents(
        this.workspaceId,
        this.requireSessionId(),
        cursor,
        this.windowPage,
        { skeleton: true },
      );
      const fresh = page.filter((e) => e.sequence_num < gap.hi);
      collected.push(...fresh);
      if (fresh.length < page.length || page.length < this.windowPage) break;
      let lastStart = collected.length - 1;
      while (lastStart > 0 && collected[lastStart].event_type !== 'reasoning_started') lastStart--;
      if (lastStart > 0) {
        collected.splice(lastStart);
        break;
      }
      cursor = page[page.length - 1].sequence_num;
    }
    this.addEvents(collected.map((ev) => this.restSkeleton(ev)));
    this.replaceEvents(capEvents(this.events, this.windowCeiling, 'oldest'));
    return collected;
  }

  /**
   * Report what one view shows. The SDK keeps the live tail plus every
   * reported view's span with one page of margin each side, under the window
   * ceiling, and releases the rest from memory and the saved history
   * (sdk ADR-f43c7). A view reports `null` when it is closed or hidden.
   */
  setViewportDemand(view: 'runs-panel', range: RunsPanelDemand | null): void;
  setViewportDemand(view: 'transcript', range: TranscriptDemand | null): void;
  setViewportDemand(
    view: 'runs-panel' | 'transcript',
    range: RunsPanelDemand | TranscriptDemand | null,
  ): void {
    if (view === 'runs-panel') this.demand.runsPanel = range as RunsPanelDemand | null;
    else this.demand.transcript = range as TranscriptDemand | null;
    this.replaceEvents(retainEvents(this.events, this.eventDemands(), this.windowPage, this.windowCeiling));
    if (this.demand.transcript !== undefined && this.transcriptInitialized) {
      const t = this.demand.transcript;
      this.replaceTranscript(
        retainMessages(
          this.transcript,
          this.transcriptTailFloor(),
          t ? { lo: t.oldestMessageIndex, hi: t.newestMessageIndex } : null,
          this.windowPage,
          this.windowCeiling,
        ),
      );
    }
  }

  private eventGap(): SeqRange | null {
    return firstGap(this.events, (e) => e.sequence_num, this.contiguousMax);
  }

  private eventDemands(): SeqRange[] {
    const ranges: SeqRange[] = [];
    const panel = this.demand.runsPanel;
    if (panel) {
      const range = this.runSeqRange(panel.oldestRunId, panel.newestRunId);
      if (range) ranges.push(range);
    }
    const transcript = this.demand.transcript;
    if (transcript) {
      const shown = new Set(
        this.transcript
          .filter(
            (m) =>
              m.message_index >= transcript.oldestMessageIndex &&
              m.message_index <= transcript.newestMessageIndex,
          )
          .map((m) => m.id),
      );
      const runs = this._state.reasoningRuns.filter(
        (run) => run.assistantMessageId !== undefined && shown.has(run.assistantMessageId),
      );
      if (runs.length > 0) {
        const range = this.runSeqRange(runs[0].runId, runs[runs.length - 1].runId);
        if (range) ranges.push(range);
      }
    }
    return ranges;
  }

  private runSeqRange(oldestRunId: string, newestRunId: string): SeqRange | null {
    const lo = this.events.find((e) => e.data.run_id === oldestRunId)?.sequence_num;
    const newestFirst = this.events.find((e) => e.data.run_id === newestRunId)?.sequence_num;
    if (lo === undefined || newestFirst === undefined) return null;
    const nextStart = this.events.find(
      (e) => e.sequence_num > newestFirst && e.event_type === 'reasoning_started',
    );
    return {
      lo: Math.min(lo, newestFirst),
      hi: nextStart ? nextStart.sequence_num - 1 : this.maxSequenceNum,
    };
  }

  private replaceEvents(next: EventSkeleton[]): void {
    if (next.length === this.events.length) return;
    const kept = new Set(next.map((e) => e.sequence_num));
    const prevOldest = this.oldestLoadedSequence;
    for (const e of this.events) {
      if (kept.has(e.sequence_num)) continue;
      this.skeletonBySeq.delete(e.sequence_num);
      this.dropPayload(e.sequence_num);
    }
    this.holdLiveBackgroundStarts(next, kept);
    this.events = next;
    if (this.oldestLoadedSequence > prevOldest) this.eventsWindowExhausted = false;
    this.fold();
    if (this._state.workflowId) this._workflowId = this._state.workflowId;
    this.prevMessageCount = this._state.messages.length;
    this.notifyChanges();
  }

  private transcriptTailFloor(): number {
    const n = this.transcript.length;
    if (n === 0) return 0;
    return this.transcript[Math.max(0, n - this.windowPage)].message_index;
  }

  private replaceTranscript(next: Message[]): void {
    if (next.length === this.transcript.length) return;
    const prevOldest = this.transcript[0]?.message_index;
    const kept = new Set(next.map((m) => m.id));
    for (const m of this.transcript) {
      if (!kept.has(m.id)) this.transcriptById.delete(m.id);
    }
    this.transcript = next;
    if (prevOldest !== undefined && (this.transcript[0]?.message_index ?? Infinity) > prevOldest) {
      this.transcriptExhausted = false;
    }
    this.persistTranscriptWindow();
    this.notifyTranscriptChanged();
  }

  private persistTranscriptWindow(): void {
    const n = this.transcript.length;
    if (n === 0) return;
    let floor = n - 1;
    while (floor > 0 && this.transcript[floor - 1].message_index === this.transcript[floor].message_index - 1) {
      floor--;
    }
    const contiguousFrom = this.transcript[floor].message_index;
    const sessionId = this.requireSessionId();
    void this.http
      .actingAccountId()
      .then((identity) => this.historyCache.dropBelow(identity, this.workspaceId, sessionId, contiguousFrom))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      });
  }

  /**
   * Pages the transcript backward until the message with this id is loaded.
   * Returns false when the transcript start is reached without finding it.
   */
  async revealMessage(messageId: string): Promise<boolean> {
    await this.loadTranscriptTail();
    while (!this.transcriptById.has(messageId) && this.hasOlderMessages) {
      await this.loadOlderMessages();
    }
    return this.transcriptById.has(messageId);
  }

  /**
   * Initial tail page of the transcript window. Idempotent; a no-op on a draft
   * (no session row exists yet).
   */
  async loadTranscriptTail(): Promise<void> {
    if (this.isDraft || this.transcriptInitialized) return;
    if (this.transcriptTailLoad) return this.transcriptTailLoad;
    this.transcriptTailLoad = this.loadTranscriptTailUncached().finally(() => {
      this.transcriptTailLoad = null;
    });
    return this.transcriptTailLoad;
  }

  /**
   * Cache-hit path: a stored page set paints
   * immediately and a background revalidate replaces the tail atomically; a
   * miss fetches and stores. Revalidate failure is quiet: the stored
   * snapshot stays, the failure is logged, reconnect heals.
   */
  private async loadTranscriptTailUncached(): Promise<void> {
    const identity = await this.http.actingAccountId();
    const sessionId = this.requireSessionId();
    const cached = this.historyCache.lookup(identity, this.workspaceId, sessionId);
    if (!cached) {
      await this.fetchTranscriptTail();
      return;
    }
    // The stored page set paints now; the revalidate runs detached, so
    // the awaited load returns the CACHED snapshot, never the refreshed one.
    this.mergeTranscript(cached.tail.messages);
    for (const p of cached.olderPages) this.mergeTranscript(p.messages);
    if (cached.exhausted) this.transcriptExhausted = true;
    this.transcriptInitialized = true;
    this.notifyTranscriptChanged();
    void this.revalidateTail(identity, sessionId);
  }

  private async revalidateTail(identity: string, sessionId: string): Promise<void> {
    try {
      // Refetch the WHOLE loaded span (plus anything new): the replacement must
      // tile exactly against the kept older pages — a 200-row tail alone would
      // strand rows between its window and the older pages when the live edge
      // moved. Bounded by what the user actually loaded.
      const cached = this.historyCache.lookup(identity, this.workspaceId, sessionId);
      const cachedRows = cached
        ? [...cached.tail.messages, ...cached.olderPages.flatMap((p) => p.messages)]
        : [];
      const oldest = cachedRows.length > 0 ? cachedRows[0].message_index : null;
      const maxIdx = cachedRows.length > 0 ? cachedRows[cachedRows.length - 1].message_index : 0;
      const limit =
        oldest === null
          ? this.windowPage
          : Math.max(this.windowPage, Math.min(5000, maxIdx - oldest + 1));
      const tail = await this.sessions.getMessagesPage(this.workspaceId, sessionId, {
        latest: true,
        limit,
      });
      const exhausted =
        tail.length < limit || (tail.length > 0 && tail[0].message_index <= 1);
      this.historyCache.replaceTail(
        identity,
        this.workspaceId,
        sessionId,
        { messages: tail, fetchedAt: Date.now() },
        exhausted,
      );
      this.mergeTranscript(tail);
      if (exhausted) this.transcriptExhausted = true;
      this.notifyTranscriptChanged();
    } catch (err) {
      // no-fallbacks.md exception: the degradation is explicit and user-visible
      // — the stale snapshot stays, the failure is logged, reconnect
      // heals; a per-failure surface error would only noise the outage.
      emitLifecycleEvent('cache.history_revalidate_failed', {
        session_id: sessionId,
        cause: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private async fetchTranscriptTail(): Promise<void> {
    const sessionId = this.requireSessionId();
    const page = await this.sessions.getMessagesPage(
      this.workspaceId,
      sessionId,
      { latest: true, limit: this.windowPage },
    );
    this.mergeTranscript(page);
    if (page.length < this.windowPage || page[0].message_index <= 1) {
      this.transcriptExhausted = true;
    }
    this.transcriptInitialized = true;
    const identity = await this.http.actingAccountId();
    this.historyCache.replaceTail(
      identity,
      this.workspaceId,
      sessionId,
      { messages: page, fetchedAt: Date.now() },
      page.length < this.windowPage,
    );
    this.notifyTranscriptChanged();
  }

  /**
   * Backward transcript page below the oldest loaded message_index.
   * Empty result = transcript start.
   */
  async loadOlderMessages(limit?: number): Promise<readonly Message[]> {
    const pageLimit = limit ?? this.windowPage;
    if (!this.hasOlderMessages || !this.transcriptInitialized) return [];
    const sessionId = this.requireSessionId();
    const cursor = this.transcript[0].message_index;
    if (cursor <= 0) {
      this.transcriptExhausted = true;
      this.notifyTranscriptChanged();
      return [];
    }
    const page = await this.sessions.getMessagesPage(
      this.workspaceId,
      sessionId,
      { beforeMessageIndex: cursor, limit: pageLimit },
    );
    this.mergeTranscript(page);
    if (page.length < pageLimit || this.transcript[0].message_index <= 0) this.transcriptExhausted = true;
    if (this.transcriptGap === null) {
      // (sdk ADR-f43c7)
      const identity = await this.http.actingAccountId();
      this.historyCache.appendOlderPage(identity, this.workspaceId, sessionId, cursor, page);
    }
    this.replaceTranscript(capMessages(this.transcript, this.transcriptTailFloor(), this.windowCeiling, 'newest'));
    this.notifyTranscriptChanged();
    return page;
  }

  /**
   * Forward transcript page that refills the released rows above the detached
   * history segment. Never saved to the history cache. Empty result = not detached.
   */
  async loadNewerMessages(): Promise<readonly Message[]> {
    const gap = this.transcriptGap;
    if (!gap || !this.transcriptInitialized) return [];
    const page = await this.sessions.getMessagesPage(this.workspaceId, this.requireSessionId(), {
      afterMessageIndex: gap.afterMessageIndex,
      limit: this.windowPage,
    });
    this.mergeTranscript(page);
    this.replaceTranscript(capMessages(this.transcript, this.transcriptTailFloor(), this.windowCeiling, 'oldest'));
    this.notifyTranscriptChanged();
    return page;
  }

  /** Reset to the latest page (used after edit-and-rewind). */
  async reloadTranscript(): Promise<void> {
    if (this.isDraft) return;
    this.transcript = [];
    this.transcriptById.clear();
    this.transcriptExhausted = false;
    this.transcriptInitialized = false;
    await this.fetchTranscriptTail();
  }

  private mergeTranscript(page: Message[]): void {
    for (const m of page) {
      if (this.transcriptById.has(m.id)) continue;
      this.transcriptById.set(m.id, m);
      this.transcript.push(m);
    }
    this.transcript.sort((a, b) => a.message_index - b.message_index);
  }

  private notifyTranscriptChanged(): void {
    for (const handler of this.transcriptHandlers) handler();
  }

  private scheduleTranscriptCatchUp(): void {
    if (this.isDraft || !this.transcriptInitialized || this.transcriptCatchUp) return;
    this.transcriptCatchUp = this.catchUpTranscript()
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      })
      .finally(() => {
        this.transcriptCatchUp = null;
      });
  }

  private async catchUpTranscript(): Promise<void> {
    const sessionId = this.requireSessionId();
    const newest = this.transcript[this.transcript.length - 1];
    const page = await this.sessions.getMessagesPage(
      this.workspaceId,
      sessionId,
      {
        afterMessageIndex: newest === undefined ? 0 : newest.message_index,
        limit: this.windowPage,
      },
    );
    if (page.length === 0) return;
    this.mergeTranscript(page);
    const identity = await this.http.actingAccountId();
    this.historyCache.extendTail(identity, this.workspaceId, sessionId, page);
    this.notifyTranscriptChanged();
  }

  private _addEventFromPush(data: Record<string, unknown>): void {
    const seq = (data.sequence_num as number) ?? 0;
    const handle = data.handle as string | undefined;
    const skeleton: EventSkeleton = {
      id: (data.id as string) ?? `push-${seq}`,
      session_id: data.session_id as string,
      event_type: data.event_type as string,
      role: (data.role as string) ?? 'system',
      sequence_num: seq,
      size_bytes: (data.size_bytes as number) ?? 0,
      data: (data.data as Record<string, unknown>) ?? {},
      handle,
      hydrated: handle === undefined,
      created_at: (data.created_at as string) ?? new Date().toISOString(),
    };

    if (this.pendingPush.push(skeleton) === 1) {
      queueMicrotask(() => {
        const batch = this.pendingPush;
        this.pendingPush = [];
        if (!this.disconnected) this.addEvents(batch);
      });
    }
  }

  private postSend(
    content: string,
    mentions?: MentionRef[],
    viewContext?: ViewContext,
    imageAttachments?: ImageAttachment[],
    participantId?: string,
  ): Promise<SendMessageResponse> {
    const clientContext = resolveClientContext();
    // Mentions DERIVE file/directory entries; images arrive already uploaded.
    // Both go in one array because there is one ordering rule for "things
    // before the user text" — so this merges rather than letting either side
    // clobber the other.
    const merged: PromptAttachment[] = [
      ...(mentions ?? []).map((m) => ({ type: m.type, path: m.path, label: m.label })),
      ...(imageAttachments ?? []),
    ];
    const attachments: PromptAttachment[] | undefined = merged.length > 0 ? merged : undefined;
    const messageId = uuidV4();
    return this.http.post<SendMessageResponse>(
      `${this.reasoningPrefix()}/send-message`,
      {
        session_id: this.requireSessionId(),
        content,
        message_id: messageId,
        ...(attachments ? { attachments } : {}),
        // Acting connection device_id: the tab
        // driving this turn. When the backend confirms it is a caller-owned live
        // connection (the device-ownership check) it refreshes
        // session.device_id to it, so platform_call execution follows the live
        // driving tab (the web app's ShogunChat drives via this same
        // ChatSession.send, so this is the client-reachable path that makes the
        // refresh fire). Omitted when the session has no device binding (plain
        // web chat / server-resolved), byte-identical to before.
        ...(this.deviceId ? { device_id: this.deviceId } : {}),
        // Per-turn view context, camel → wire
        // `view_context`. Only attached when supplied, so a plain send is
        // byte-identical to before.
        ...(viewContext ? { view_context: viewContext } : {}),
        // The client's own environment. Resolved
        // HERE rather than at `send()` so a message that auto-queued behind a
        // running turn carries the timezone in effect when it actually flushes,
        // not when it was typed. Inert data — it renders one prompt line
        // and never influences tenancy or routing. Omitted entirely when the
        // runtime cannot name a zone, so the precedence falls through to the
        // stored profile structurally (omit-when-unknown) rather than
        // sending an empty string the backend would have to special-case.
        ...(clientContext ? { client_context: clientContext } : {}),
        ...(participantId !== undefined ? { participant_id: participantId } : {}),
      },
    );
  }

  private addEvents(batch: readonly EventSkeleton[]): void {
    let inserted = false;
    let queueChanged = false;
    const primaryId = this.primaryParticipantId;
    for (const event of batch) {
      if (this.insertEvent(event)) inserted = true;
      if (event.event_type === 'tasks_updated' && !this.isDraft) {
        const version = (event.data as { version?: unknown }).version;
        if (typeof version !== 'number') this.reportError(new Error(`tasks_updated event ${event.id} carries no version`));
        else if (this._tasks === null || version > this._tasks.version) this._loadTasks().catch((err: unknown) => this.reportError(err));
      }
      if (event.event_type === 'queue_updated') {
        const data = event.data as { version?: number; participant_id?: string } | undefined;
        const version = data?.version;
        const pid = data?.participant_id;
        if (pid && primaryId !== null && pid !== primaryId) {
          const entry = this.participantQueues.get(pid);
          if (entry && (version === undefined || version > entry.version)) this.loadParticipantQueue(pid);
          else if (!entry && this.participantImageHolds.has(pid)) this.trackParticipantQueue(pid);
          continue;
        }
        if (version === undefined || version > this._queueVersion) queueChanged = true;
      }
    }
    if (queueChanged && !this.isDraft) {
      this._loadQueue().catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      });
    }
    if (!inserted) return;
    this.advanceContiguousMax();

    const unknownParticipant = this.recordParticipantStatuses(batch);
    this.fold();
    this.catchUpParticipantTranscripts(batch);
    if (unknownParticipant || this.endsSnapshotBackgroundTask(batch)) this.refreshParticipants();
    if (this._state.workflowId) {
      this._workflowId = this._state.workflowId;
    }

    this.notifyChanges();
    this.pruneLiveCalls();
  }

  private insertEvent(event: EventSkeleton): boolean {
    // Deduplicate by sequence_num (O(1) via the skeleton index) — replay-vs-live
    // dedup. Correct regardless of which payloads happen to be cached.
    if (this.skeletonBySeq.has(event.sequence_num)) {
      return false;
    }
    // Gap DETECTION: a live frame whose seq jumps past maxSequenceNum + 1
    // means we missed events between. Debounced same-socket reregister refills
    // the hole via replay from contiguousMax. Guarded on maxSequenceNum > 0 so
    // the first-ever event (or a late first frame) is handled by the initial
    // subscribe replay, not a spurious reregister.
    if (this.maxSequenceNum > 0 && event.sequence_num > this.maxSequenceNum + 1) {
      this.scheduleGapReregister();
    }

    insertBySequence(this.events, event);
    this.skeletonBySeq.set(event.sequence_num, event);
    if (event.sequence_num > this.maxSequenceNum) {
      this.maxSequenceNum = event.sequence_num;
    }

    // Live context_health demux — fire
    // dedicated handlers BEFORE progress-tree reprocess so the Context tab can
    // merge without waiting on processEvents (which ignores this type).
    if (event.event_type === 'context_health' && this.contextHealthHandlers.size > 0) {
      const raw = event.data?.sources;
      const sources = Array.isArray(raw)
        ? raw
            .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
            .map((s) => {
              const sourceId = s.source_id;
              const health = s.health;
              return {
                source_id: typeof sourceId === 'string' ? sourceId : '',
                health: typeof health === 'string' ? health : '',
              };
            })
            .filter((s) => s.source_id !== '' && s.health !== '')
        : [];
      for (const handler of this.contextHealthHandlers) {
        handler({ sources });
      }
    }
    return true;
  }

  private _applyLlmProgress(frame: LlmProgressFrame): void {
    if (!frame.call_id || this._settledCallIds.has(frame.call_id)) return;
    const prev = this._liveCalls.get(frame.call_id);
    if (prev && frame.seq <= prev.seq) return;
    const base: LiveLlmCall =
      prev && !frame.reset
        ? prev
        : {
            callId: frame.call_id,
            taskId: frame.task_id,
            runId: frame.run_id,
            phase: 'reasoning',
            reasoning: '',
            content: '',
            toolArgs: '',
            reasoningChunks: 0,
            contentChunks: 0,
            toolArgsChunks: 0,
            seq: 0,
            settled: false,
            state: frame.state,
            elapsedMs: frame.elapsed_ms,
            receivedAt: Date.now(),
            attempt: frame.attempt,
            outputChunks: frame.output_chunks,
            nested: frame.nested,
          };
    const toolChanged = frame.tool_name !== '' && frame.tool_name !== base.toolName;
    this._liveCalls.set(frame.call_id, {
      ...base,
      phase: frame.phase === '' ? base.phase : frame.phase,
      toolName: frame.tool_name !== '' ? frame.tool_name : base.toolName,
      reasoning: base.reasoning + frame.reasoning_delta,
      content: base.content + frame.content_delta,
      toolArgs: (toolChanged ? '' : base.toolArgs) + frame.tool_args_delta,
      reasoningChunks: frame.reasoning_chunks,
      contentChunks: frame.content_chunks,
      toolArgsChunks: frame.tool_args_chunks,
      seq: frame.seq,
      state: frame.state,
      elapsedMs: frame.elapsed_ms,
      receivedAt: Date.now(),
      attempt: frame.attempt,
      outputChunks: frame.output_chunks,
      outputTokens: frame.output_tokens,
      nested: frame.nested,
      failure: frame.failure,
    });
    const parentRunEnded =
      (frame.state === 'failed' || frame.state === 'stalled') &&
      !this._state.reasoningRuns.some((run) => run.status === 'in_progress');
    if (frame.nested && (frame.state === 'done' || parentRunEnded || this.nestedParentHasResult(frame.task_id))) {
      this._liveCalls.delete(frame.call_id);
      this._settledCallIds.add(frame.call_id);
    }
    this.notifyLiveCalls();
  }

  private pruneLiveCalls(): void {
    const anyOpen = this._state.reasoningRuns.some((run) => run.status === 'in_progress');
    if (!anyOpen) {
      this._settledCallIds.clear();
      this._assistantCountAtSettle.clear();
      if (this._liveCalls.size === 0) return;
      this._liveCalls.clear();
      this.notifyLiveCalls();
      return;
    }
    const assistantCount = this._state.messages.filter((m) => m.role === 'assistant').length;
    let changed = false;
    for (const [callId, call] of this._liveCalls) {
      if (call.nested) {
        if (this.nestedParentHasResult(call.taskId)) {
          this._liveCalls.delete(callId);
          this._settledCallIds.add(callId);
          changed = true;
        }
        continue;
      }
      if (call.settled) {
        if (assistantCount > (this._assistantCountAtSettle.get(callId) ?? assistantCount)) {
          this._liveCalls.delete(callId);
          this._assistantCountAtSettle.delete(callId);
          changed = true;
        }
        continue;
      }
      if (!this.callHasSettled(callId)) continue;
      this._settledCallIds.add(callId);
      changed = true;
      if (this.isRootAnswer(call)) {
        this._liveCalls.set(callId, { ...call, settled: true });
        this._assistantCountAtSettle.set(callId, assistantCount);
      } else {
        this._liveCalls.delete(callId);
      }
    }
    if (changed) this.notifyLiveCalls();
  }

  private isRootAnswer(call: LiveLlmCall): boolean {
    if (call.content === '' || call.toolArgs !== '') return false;
    return this._state.reasoningRuns.some(
      (run) => run.status === 'in_progress' && run.nodes.some((n) => n.id === call.taskId && !n.parentId),
    );
  }

  private nestedParentHasResult(childId: string): boolean {
    for (const run of this._state.reasoningRuns) {
      const steps = [...run.steps, ...run.nodes.flatMap((n) => n.steps)];
      if (
        steps.some(
          (step) =>
            step.childIdAtStart === childId && (step.toolResultStatus != null || step.toolResultError != null),
        )
      ) {
        return true;
      }
    }
    return false;
  }

  private callHasSettled(callId: string): boolean {
    for (const run of this._state.reasoningRuns) {
      const steps = [...run.steps, ...run.nodes.flatMap((n) => n.steps)];
      if (steps.some((step) => step.callId === callId && step.status !== 'running')) return true;
    }
    return false;
  }

  private notifyLiveCalls(): void {
    const calls = this.liveLlmCalls;
    for (const handler of this.liveCallHandlers) handler(calls);
  }

  private advanceContiguousMax(): void {
    for (let i = indexAfter(this.events, this.contiguousMax); i < this.events.length; i++) {
      if (this.events[i].sequence_num !== this.contiguousMax + 1) break;
      this.contiguousMax++;
    }
  }

  private scheduleGapReregister(): void {
    if (!this.sessionSource) return; // desktop/daemon arm has no reregister
    if (this.gapReregisterTimer !== null) return; // already scheduled (debounce)
    this.gapReregisterTimer = setTimeout(() => {
      this.gapReregisterTimer = null;
      this.sessionSource?.reregister(this.requireSessionId()).catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        for (const handler of this.errorHandlers) handler(msg);
      });
    }, ChatSession.GAP_REREGISTER_DEBOUNCE_MS);
  }

  /**
   * Read what a run-start retrieval step gave the model: each `hit_refs` entry
   * of a `context_rag_completed` event, resolved to its stored entry as the
   * seed inlined it (see {@link SessionsResource.readContextEntries}). Returns
   * one entry per ref, in order; a ref whose entry is gone is `found: false`.
   * Rejects on any request failure — never an empty list (no-fallbacks).
   */
  async readRagHits(refs: RagHitRef[]): Promise<ContextEntryRead[]> {
    return this.sessions.readContextEntries(this.workspaceId, this.requireSessionId(), refs);
  }

  /**
   * Return one event's FULL `data`, hydrating on demand. A small/inline event (no `handle`) returns its skeleton `data`
   * directly. A large event returns the cached full payload, or hydrates it via
   * the completion-looping {@link SessionsResource.readEventPayload}, caches it
   * (LRU), and returns it. On failure it sets `hydrationError` — `not_available`
   * for a 404 (the event is gone), `failed` for a network/Temporal error — and
   * RETHROWS (never a silent empty render, no-fallbacks).
   */
  async getEventData(seq: number): Promise<Record<string, unknown>> {
    const skel = this.skeletonBySeq.get(seq);
    if (!skel) {
      throw new SDKError(`getEventData: no event with sequence_num ${seq} in this session`);
    }
    if (skel.handle === undefined) {
      return skel.data;
    }
    const cached = this.payloadCache.get(seq);
    if (cached !== undefined) {
      this.touchCache(seq);
      return cached;
    }
    try {
      const full = await this.sessions.readEventPayload(this.workspaceId, this.requireSessionId(), seq);
      this.cachePut(seq, full);
      skel.hydrationError = undefined;
      skel.hydrated = true;
      return full;
    } catch (err) {
      skel.hydrationError = isNotFound(err) ? 'not_available' : 'failed';
      throw err;
    }
  }

  /**
   * Pre-hydrate a WINDOW of events in one batch request (scroll-into-view
   * — a single {@link SessionsResource.readEventPayloads}, not N calls). Only
   * large, not-yet-cached events are fetched; complete entries are cached, and a
   * partial first page is left for a later {@link getEventData} to complete.
   * Best-effort pre-fetch: a batch failure surfaces to error handlers, and each
   * individual event can still hydrate on click.
   */
  async hydrateWindow(seqs: number[]): Promise<void> {
    const missing = seqs.filter((s) => {
      const skel = this.skeletonBySeq.get(s);
      return skel !== undefined && skel.handle !== undefined && !this.payloadCache.has(s);
    });
    if (missing.length === 0) return;
    const entries = await this.sessions.readEventPayloads(this.workspaceId, this.requireSessionId(), missing);
    for (const entry of entries) {
      if (entry.data !== null) {
        this.cachePut(entry.sequence_num, entry.data);
        const skel = this.skeletonBySeq.get(entry.sequence_num);
        if (skel) skel.hydrated = true;
      }
    }
  }

  // --- Bounded LRU payload cache ---

  private dropPayload(seq: number): void {
    if (!this.payloadCache.has(seq)) return;
    this.payloadCacheTotalBytes -= this.payloadCacheBytes.get(seq) ?? 0;
    this.payloadCache.delete(seq);
    this.payloadCacheBytes.delete(seq);
  }

  private estimatePayloadBytes(data: Record<string, unknown>): number {
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  private cachePut(seq: number, data: Record<string, unknown>): void {
    if (this.payloadCache.has(seq)) {
      this.payloadCacheTotalBytes -= this.payloadCacheBytes.get(seq) ?? 0;
      this.payloadCache.delete(seq);
      this.payloadCacheBytes.delete(seq);
    }
    const bytes = this.estimatePayloadBytes(data);
    this.payloadCache.set(seq, data); // inserted at the tail (most-recent)
    this.payloadCacheBytes.set(seq, bytes);
    this.payloadCacheTotalBytes += bytes;
    this.evictCache();
  }

  private touchCache(seq: number): void {
    const data = this.payloadCache.get(seq);
    if (data === undefined) return;
    this.payloadCache.delete(seq);
    this.payloadCache.set(seq, data);
  }

  private evictCache(): void {
    while (
      this.payloadCache.size > ChatSession.CACHE_MAX_ENTRIES ||
      this.payloadCacheTotalBytes > ChatSession.CACHE_MAX_BYTES
    ) {
      const oldest = this.payloadCache.keys().next();
      if (oldest.done) break;
      const seq = oldest.value;
      this.payloadCacheTotalBytes -= this.payloadCacheBytes.get(seq) ?? 0;
      this.payloadCache.delete(seq);
      this.payloadCacheBytes.delete(seq);
      const skel = this.skeletonBySeq.get(seq);
      if (skel) skel.hydrated = false; // re-hydratable by (session, seq)
    }
  }

  private notifyChanges(): void {
    const { messages, state, progressTree, questions, waitingOnUser } = this._state;

    this._optimistic = this._optimistic.filter(
      (row) => !messages.some((m) => m.role === 'user' && m.content === row.content),
    );
    if (this._starting && (state === 'reasoning' || state === 'error' || state === 'stopped')) {
      this._starting = false;
    }

    if (messages.length > this.prevMessageCount) {
      const newMessages = messages.slice(this.prevMessageCount);
      for (const msg of newMessages) {
        for (const handler of this.messageHandlers) handler(msg);
      }
      this.prevMessageCount = messages.length;
      this.scheduleTranscriptCatchUp();
    }

    const reported = this.state;
    if (reported !== this.prevState) {
      for (const handler of this.stateHandlers) handler(reported);

      this.prevState = reported;
    }

    if (progressTree) {
      for (const handler of this.progressHandlers) handler(progressTree);
    }

    const questionsSignature = `${waitingOnUser}|${[...questions.values()]
      .map((q) => `${q.questionId}:${q.status}:${q.addresseeKind}:${q.addresseeParticipantId ?? ''}`)
      .join(',')}`;
    if (questionsSignature !== this.prevQuestionsSignature) {
      this.prevQuestionsSignature = questionsSignature;
      const open = this.questions;
      for (const handler of this.questionsHandlers) handler(open);
    }

    if (state === 'error' && progressTree?.error) {
      for (const handler of this.errorHandlers) handler(progressTree.error);
    }

    this.notifyParticipants();
    this.notifyBackgroundWork();
  }

  private notifyBackgroundWork(): void {
    if (this.backgroundWorkHandlers.size === 0) return;
    const items = this.backgroundWork;
    const signature = items.map((i) => `${i.id}|${i.status}|${i.label}|${i.detail}|${i.replyMessageId ?? ''}|${i.startedAt ?? ''}|${i.endedAt ?? ''}`).join('\n');
    if (signature === this.backgroundWorkSignature) return;
    this.backgroundWorkSignature = signature;
    for (const handler of this.backgroundWorkHandlers) handler(items);
  }

  private notifyQueueChange(): void {
    const snapshot = [...this._queue];
    for (const handler of this.queueHandlers) handler(snapshot);
  }

  private setConnectionState(state: ConnectionState): void {
    if (this._connectionState === state) return;
    this._connectionState = state;
    for (const handler of this.connectionHandlers) handler(state);
  }
}
